/* ============================================================
   VITALCASA CRM — ModalCompletarCamposAgente
   Modal dinámico para que el agente complete los campos
   obligatorios en la etapa "Visita Concertada".
   Lee campos_obligatorios_propiedad (responsable = agente)
   y permite guardarlos inline. Auto-avanza a Para Revisar
   cuando todos están rellenos.
   ============================================================ */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from './ThemeProvider';
import VisorImagenDocumento from './captaciones/VisorImagenDocumento';
import AddressAutocompleteInput from './AddressAutocompleteInput';
import api from '../services/api';
import { postCaptacionMultipart, resolveCaptacionMediaUrl } from '../services/captacionUpload';
import { etiquetaTipoDocumento } from '../config/captacionDocumentosTipos';
import {
  getAuxSlugForCampoKey,
  normalizeAuxSelectValue,
  ordenarOpcionesTipoEquipoDesplegable,
  buildAuxExtrasPatch,
  normalizeVistasIdsFromExtras,
  buildVistasExtrasPatch,
  isVistasMultiCampo,
} from '../config/auxCamposPipeline';
import MultiSelectAuxVistas from './MultiSelectAuxVistas';
import { useAuxListsForCampos } from '../hooks/useAuxListsForCampos';
import { mapFotoCaptacionDesdeApi, urlFotoGrid, urlFotoCompleta } from '../utils/fotoMultimediaUtils';
import { mostrarAvisoVitalCasa } from '../utils/mostrarAvisoVitalCasa';
import { crearMiniaturaDesdeFile } from '../utils/fotoThumbUtils';
import { tipoInmuebleLegible, deberFotosCaptacionDisplay, deberVideosCaptacionDisplay } from '../utils/captacionDisplay';
import GarajePlazasButtons, {
  buildGarajePlazasPatch,
  isGarajePlazasKey,
  parseNumPlazasGaraje,
} from './propiedades/GarajePlazasButtons';
import {
  resolverProvinciaId,
  resolverPoblacionIdEnLista,
  resolverPoblacionPlaces,
  normLocalidadNombre,
} from '../utils/resolverLocalidadDesdePlaces';
import { parseTipoViaCalle } from '../utils/parseTipoViaCalle';
import {
  cpPorPoblacionId,
  POBLACIONES_FAVORITAS_ALICANTE,
} from '../utils/poblacionesFavoritas';
import {
  BRD_MOVIL,
  LABEL_MOVIL,
  DETAIL_KEYS_OCULTAS,
  campoTieneValor,
  claseInpMovil,
  estiloSelBtnMovil,
  padreDeDetailKey,
  toggleUsaModalDetalle,
  toggleUsaModalFlags,
  toggleUsaModalSelector,
  toggleUsaModalVinculo,
  TOGGLE_FLAGS_CFG,
  TOGGLE_SELECTOR_CFG,
} from './captacion/agenteCamposVinculados';
import InpCampoMovil from './captacion/InpCampoMovil';
import usePermiso from '../hooks/usePermiso';
import { useAuth } from '../context/AuthContext';
import ModalVinculosAgente from './captacion/ModalVinculosAgente';
import BloquePropietariosAgente, {
  hidratarPropietariosDesdeExtras,
  patchPropietariosParaExtras,
  esCampoPropietarioConfig,
} from './captacion/BloquePropietariosAgente';
import { faltaTelefonoPrincipalEnState, validarTelefonoPrincipalState } from '../utils/propietarioCamposConfig';
import { useCaptacionRelacionContacto } from '../hooks/useCaptacionRelacionContacto';
import ModalRelacionContactoExistente from './ModalRelacionContactoExistente';
import ModalAvisoRetrocesoVisita from './ModalAvisoRetrocesoVisita';
import {
  yaVioRetrocesoVisita,
  marcarRetrocesoVisitaVisto,
  esRetrocesoVisitaReciente,
} from '../utils/retrocesoVisitaVisto';
import { valorCampoAgenteEsRelleno, valorFirmaCaptacionEsRelleno } from '../utils/campoAgenteCubierto';
import './captacion/agenteMovilCampos.css';
const PROVINCIAS_ES = [
  { id: '01', nombre: 'Álava' }, { id: '02', nombre: 'Albacete' }, { id: '03', nombre: 'Alicante' },
  { id: '04', nombre: 'Almería' }, { id: '05', nombre: 'Ávila' }, { id: '06', nombre: 'Badajoz' },
  { id: '07', nombre: 'Baleares' }, { id: '08', nombre: 'Barcelona' }, { id: '09', nombre: 'Burgos' },
  { id: '10', nombre: 'Cáceres' }, { id: '11', nombre: 'Cádiz' }, { id: '12', nombre: 'Castellón' },
  { id: '13', nombre: 'Ciudad Real' }, { id: '14', nombre: 'Córdoba' }, { id: '15', nombre: 'A Coruña' },
  { id: '16', nombre: 'Cuenca' }, { id: '17', nombre: 'Girona' }, { id: '18', nombre: 'Granada' },
  { id: '19', nombre: 'Guadalajara' }, { id: '20', nombre: 'Guipúzcoa' }, { id: '21', nombre: 'Huelva' },
  { id: '22', nombre: 'Huesca' }, { id: '23', nombre: 'Jaén' }, { id: '24', nombre: 'León' },
  { id: '25', nombre: 'Lleida' }, { id: '26', nombre: 'La Rioja' }, { id: '27', nombre: 'Lugo' },
  { id: '28', nombre: 'Madrid' }, { id: '29', nombre: 'Málaga' }, { id: '30', nombre: 'Murcia' },
  { id: '31', nombre: 'Navarra' }, { id: '32', nombre: 'Ourense' }, { id: '33', nombre: 'Asturias' },
  { id: '34', nombre: 'Palencia' }, { id: '35', nombre: 'Las Palmas' }, { id: '36', nombre: 'Pontevedra' },
  { id: '37', nombre: 'Salamanca' }, { id: '38', nombre: 'S.C. Tenerife' }, { id: '39', nombre: 'Cantabria' },
  { id: '40', nombre: 'Segovia' }, { id: '41', nombre: 'Sevilla' }, { id: '42', nombre: 'Soria' },
  { id: '43', nombre: 'Tarragona' }, { id: '44', nombre: 'Teruel' }, { id: '45', nombre: 'Toledo' },
  { id: '46', nombre: 'Valencia' }, { id: '47', nombre: 'Valladolid' }, { id: '48', nombre: 'Vizcaya' },
  { id: '49', nombre: 'Zamora' }, { id: '50', nombre: 'Zaragoza' }, { id: '51', nombre: 'Ceuta' }, { id: '52', nombre: 'Melilla' },
];

const hoyISO = () => new Date().toISOString().slice(0, 10);

const CAMPOS_EXCLUSIVA_DEP = ['exclusiva_duracion', 'duracion_exclusiva', 'exclusiva_desde', 'exclusiva_hasta'];
const OPCIONES_OPERACION = ['Venta', 'Alquiler', 'Traspaso'];

const isMoneyField = (k = '') => {
  const key = String(k).toLowerCase();
  return (key.includes('precio') || key.includes('importe') || key.includes('valor')) && !key.includes('porcentaje');
};

const parseMoney = (value) => {
  const digits = String(value ?? '').replace(/[^\d]/g, '');
  return digits ? Number(digits) : null;
};

const formatMoney = (value) => {
  const n = parseMoney(value);
  if (n === null) return '';
  return `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(n)} €`;
};

/** Valor mostrado en input: state local o fallback captación/ficha (trata '' como vacío). */
function valorVisibleCampo(campo_key, valores, captacion, propiedad) {
  const raw = valores?.[campo_key];
  if (raw != null && String(raw).trim() !== '') return raw;
  return resolverValorCampoAgente(campo_key, captacion, propiedad) ?? '';
}

function aliasesCampoAgente(key) {
  const k = String(key || '');
  if (!k) return [];
  const base = k.replace(/_id$/i, '');
  return [...new Set([k, base, `${base}_id`])];
}

/** El modal ya hidrató Calle/Alicante/CP: no pintar «falta» si hay valor usable. */
function campoAgenteYaCubierto(campo, valores, captacion, propiedad) {
  const keys = aliasesCampoAgente(campo?.campo_key);
  return keys.some((k) => {
    const v = valorVisibleCampo(k, valores, captacion, propiedad);
    // firma_captacion='pendiente' (ViaFirma enviado, sin firmar) NO es cubierto:
    // antes activaba el botón verde «Avanzar a Para Revisar» con falta===0 (Mónica Ibañez).
    return valorCampoAgenteEsRelleno(k, v);
  });
}

function dedupeCamposAliasAgente(list) {
  const seen = new Set();
  const preferId = [...(list || [])].sort((a, b) => {
    const aId = String(a.campo_key || '').endsWith('_id') ? 0 : 1;
    const bId = String(b.campo_key || '').endsWith('_id') ? 0 : 1;
    return aId - bId;
  });
  const out = [];
  for (const c of preferId) {
    if (aliasesCampoAgente(c.campo_key).some((k) => seen.has(k))) continue;
    aliasesCampoAgente(c.campo_key).forEach((k) => seen.add(k));
    out.push(c);
  }
  return out;
}

// Devuelve el tipo de input a renderizar para un campo
function getTipo(campo_key, tipo_campo) {
  const k = campo_key.toLowerCase();
  if (k === 'firma_captacion') return 'encargo';
  if (isGarajePlazasKey(k)) return 'garaje_plazas';
  if (['disponible_desde', 'exclusiva_desde', 'exclusiva_hasta'].includes(k)) return 'date';
  if (tipo_campo && ['boolean', 'toggle', 'checkbox'].includes(tipo_campo)) return 'boolean';
  if ([
      'publicar_web', 'firma_captacion', 'exclusiva', 'disponible', 'visible_web', 'amueblado', 'vpo',
      'eq_piscina_priv', 'eq_piscina_comun', 'eq_ascensor', 'piscina_priv', 'piscina_comun', 'ascensor',
      'piscina_privada', 'piscina_comunitaria', 'trastero', 'terraza', 'jardin', 'aire_acondicionado',
    ].some(n => k === n || k === n.replace(/_/g, ''))) return 'boolean';
  // Número de portal y partes de dirección: texto libre (Google puede devolver «nº12», «12-B», etc.)
  if (['numero', 'bloque', 'escalera', 'puerta', 'portal', 'piso', 'planta'].includes(k)) return 'text';
  if (tipo_campo && ['numero', 'decimal', 'integer'].includes(tipo_campo)) return 'number';
  if (['precio', 'honorarios', 'm2', 'metros', 'superficie', 'habitaciones', 'banos',
       'planta', 'antiguedad'].some(n => k.includes(n))) return 'number';
  if (/^num_(dormitorios|banos|garajes|plazas)/.test(k)) return 'number';
  if (tipo_campo === 'fecha' || k.includes('fecha') || k.includes('date')) return 'date';
  if (tipo_campo === 'textarea' || ['resumen', 'descripcion', 'notas', 'observaciones']
      .some(n => k.includes(n))) return 'textarea';
  return 'text';
}

const API_ORIGIN_AGENTE =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL
    ? String(import.meta.env.VITE_API_URL).replace(/\/$/, '')
    : 'http://localhost:4000';

function absolutizarMediaUrlAgente(u) {
  if (!u || typeof u !== 'string') return '';
  const t = u.trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  return API_ORIGIN_AGENTE + (t.startsWith('/') ? '' : '/') + t;
}

function docVistaPreviaKindAgente(doc, urlFallback = '') {
  const mime = String(doc?.mime || '').toLowerCase();
  const name = String(doc?.nombre_documento || doc?.nombre_original || urlFallback || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (name.endsWith('.pdf')) return 'pdf';
  if (/\.(jpe?g|png|webp|gif)$/.test(name)) return 'image';
  return 'other';
}

/** Dirección: un solo bloque visual. La vía va primero para Google Places. */
const ADDR_GROUP_KEYS = [
  'nombre_via', 'calle',
  'tipo_via_id', 'tipo_via',
  'numero', 'piso', 'planta', 'puerta', 'portal', 'escalera', 'bloque',
  'codigo_postal',
  'provincia_id', 'provincia',
  'poblacion_id', 'poblacion',
  'zona_id', 'zona',
];

const ADDR_GROUP_KEY_SET = new Set(ADDR_GROUP_KEYS);

/** Títulos como en la app móvil (no claves técnicas). */
const LABEL_AGENTE_OVERRIDE = {
  nombre_via: 'Dirección',
  calle: 'Dirección',
  tipo_via_id: 'Tipo de vía',
  tipo_via: 'Tipo de vía',
  numero: 'Número',
  piso: 'Planta',
  planta: 'Planta',
  puerta: 'Puerta',
  portal: 'Portal',
  escalera: 'Escalera',
  bloque: 'Bloque',
  codigo_postal: 'Código postal',
  poblacion_id: 'Población',
  poblacion: 'Población',
  provincia_id: 'Provincia',
  provincia: 'Provincia',
  zona_id: 'Zona',
  zona: 'Zona',
  orientacion_id: 'Orientación',
  orientacion: 'Orientación',
  eq_aire_id: 'Aire acondicionado',
  eq_aire: 'Aire acondicionado',
  eq_calefaccion_id: 'Calefacción',
  eq_calefaccion: 'Calefacción',
  eq_piscina_priv_id: 'Piscina privada',
  eq_piscina_priv: 'Piscina privada',
  eq_piscina_comun_id: 'Piscina comunitaria',
  eq_piscina_comun: 'Piscina comunitaria',
  eq_trastero: 'Trastero',
  eq_garaje: 'Garaje',
  vpo: 'VPO',
  cocina_amueblada: 'Cocina amueblada',
  armarios: 'Armarios',
  muebles_id: 'Amueblado',
  suelos_id: 'Suelos',
  cocina_id: 'Cocina',
  estado_id: 'Estado',
  carpinteria_int_id: 'Carpintería interior',
  carpinteria_ext_id: 'Carpintería exterior',
  num_plazas_garaje: 'Nº garajes',
  num_dormitorios: 'Dormitorios',
  num_banos: 'Baños',
  num_aseos: 'Aseos',
  tipo_inmueble_id: 'Tipo de inmueble',
  operacion: 'Operación',
  agente_id: 'Agente',
  firma_captacion: 'Encargo de venta',
  fotos: 'Fotos',
  documentos: 'Documentos',
  propietario: 'Propietario',
  ascensor: 'Ascensor',
  eq_ascensor: 'Ascensor',
};

function limpiarLabelAgente(label) {
  return String(label || '')
    .replace(/^chips\s+selector\s+/i, '')
    .replace(/^btn[_\s-]?selector\s+/i, '')
    .replace(/^selector\s+/i, '')
    .replace(/\s+selector$/i, '')
    .replace(/\bselector\b/gi, ' ')
    .replace(/^tipo\s+(de\s+)?/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function humanizarClaveCampo(key) {
  return String(key || '')
    .replace(/_id$/i, '')
    .replace(/^eq_/i, '')
    .replace(/^ub_/i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Fotos, documentos, encargo y campos propietario (bloque N-2) tienen UI propia. */
function esCampoUiEspecialAgente(campoKey) {
  return (
    campoKey === 'fotos'
    || campoKey === 'documentos'
    || campoKey === 'firma_captacion'
    || campoKey === 'nota_simple'
    || campoKey === 'nota_simple_id'
    || esCampoPropietarioConfig(campoKey)
  );
}

function esCampoNotaSimpleDoc(campoKey) {
  return campoKey === 'nota_simple' || campoKey === 'nota_simple_id';
}

/** Etiqueta de negocio: misma semántica que el móvil (sin prefijo «Tipo»). */
function labelCampoAgente(campo) {
  if (!campo) return '';
  const key = String(campo.campo_key || '');
  if (LABEL_AGENTE_OVERRIDE[key]) return LABEL_AGENTE_OVERRIDE[key];
  const raw = limpiarLabelAgente(campo.campo_label || '');
  if (!raw) return humanizarClaveCampo(key);
  const rawNorm = raw.replace(/\s+/g, '_').toLowerCase();
  if (rawNorm === key.toLowerCase() || /^[A-Z0-9_]+$/.test(String(campo.campo_label || ''))) {
    return LABEL_AGENTE_OVERRIDE[key] || humanizarClaveCampo(key);
  }
  return raw;
}

function esClaveZonaAgente(campoOrKey) {
  const key = typeof campoOrKey === 'string' ? campoOrKey : campoOrKey?.campo_key;
  return key === 'zona' || key === 'zona_id';
}

function textoEsDeniaAgente(v) {
  const t = String(v || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (!t) return false;
  if (t === 'denia') return true;
  return /(^|[^a-z])denia([^a-z]|$)/.test(t);
}

/** Zona solo es obligatoria en Dénia (misma regla que campoRelleno / APP). */
function esPoblacionDeniaAgente(cap, extras, valores) {
  const nombres = [
    valores?.poblacion,
    extras?.poblacion,
    extras?.poblacion_nombre,
    cap?.poblacion,
    cap?._poblacion_nombre_prop,
  ];
  if (nombres.some((n) => textoEsDeniaAgente(n))) return true;
  const nombreResuelto = nombres
    .map((n) => String(n || '').trim())
    .find((t) => t && !/^\d{4,5}$/.test(t));
  if (nombreResuelto && !textoEsDeniaAgente(nombreResuelto)) return false;
  const rawId = String(
    valores?.poblacion_id || extras?.poblacion_id || cap?.poblacion_id || '',
  ).trim();
  if (rawId) {
    const idNorm = /^\d+$/.test(rawId) ? rawId.padStart(5, '0') : rawId;
    if (idNorm === '03063' || idNorm === '03070') return true;
  }
  return false;
}

function esClaveDireccionAgente(campoOrKey) {
  const key = typeof campoOrKey === 'string' ? campoOrKey : campoOrKey?.campo_key;
  if (!key) return false;
  if (ADDR_GROUP_KEY_SET.has(key)) return true;
  const grupo = String(
    (typeof campoOrKey === 'object' && campoOrKey?.campo_grupo) || '',
  ).toLowerCase();
  return /direcci/.test(grupo);
}

/** Agrupa dirección en un bloque; el resto conserva orden (exclusiva deps juntos). */
function agruparCamposPendientesAgente(campos) {
  const list = Array.isArray(campos) ? campos : [];
  const addr = list.filter((c) => esClaveDireccionAgente(c));
  if (!addr.length) {
    return list.map((c) => ({ type: 'field', campo: c }));
  }
  const orderIdx = new Map(ADDR_GROUP_KEYS.map((k, i) => [k, i]));
  addr.sort((a, b) => {
    const ia = orderIdx.has(a.campo_key) ? orderIdx.get(a.campo_key) : 999;
    const ib = orderIdx.has(b.campo_key) ? orderIdx.get(b.campo_key) : 999;
    return ia - ib;
  });
  const firstAddrIdx = list.findIndex((c) => esClaveDireccionAgente(c));
  const blocks = [];
  let addrInserted = false;
  list.forEach((c, i) => {
    if (esClaveDireccionAgente(c)) {
      if (!addrInserted && i === firstAddrIdx) {
        blocks.push({ type: 'group', grupo: 'direccion', titulo: 'Dirección', campos: addr });
        addrInserted = true;
      }
      return;
    }
    blocks.push({ type: 'field', campo: c });
  });
  if (!addrInserted) {
    blocks.unshift({ type: 'group', grupo: 'direccion', titulo: 'Dirección', campos: addr });
  }
  return blocks;
}

// Obtiene el valor actual del campo desde la captación
function getValorActual(campo_key, captacion) {
  const extrasRaw = (typeof captacion.datos_extras === 'object' && captacion.datos_extras !== null)
    ? captacion.datos_extras
    : (() => { try { return JSON.parse(captacion.datos_extras || '{}'); } catch { return {}; } })();
  const nested = extrasRaw.campos && typeof extrasRaw.campos === 'object' && !Array.isArray(extrasRaw.campos)
    ? extrasRaw.campos
    : {};
  const extras = { ...nested, ...extrasRaw };

  const pobTexto = (() => {
    for (const c of [extras.poblacion_nombre, extras.poblacion, captacion.poblacion]) {
      const s = String(c || '').trim();
      if (s && !/^\d{4,5}$/.test(s)) return s;
    }
    return extras.domicilio_poblacion || '';
  })();
  const MAPA_NATIVO = {
    nombre_via:       captacion.calle || extras.nombre_via || extras.calle || '',
    calle:            captacion.calle || extras.calle || extras.nombre_via || '',
    numero:           captacion.numero ?? extras.numero ?? '',
    bloque:           extras.bloque || '',
    escalera:         extras.escalera || '',
    piso:             extras.piso || extras.planta || extras.altura || '',
    planta:           extras.planta || extras.piso || extras.altura || '',
    puerta:           extras.puerta || '',
    poblacion_id:     extras.poblacion_id || '',
    poblacion:        pobTexto,
    tipo_inmueble_id: captacion.tipo_inmueble,
    tipo_inmueble:    captacion.tipo_inmueble,
    operacion:        captacion.operacion,
    agente_id:        extras.agente_id || captacion.agente_id || '',
  };
  if (campo_key === 'poblacion_id') {
    const rawId = extras.poblacion_id || captacion.poblacion_id || '';
    if (rawId && (/^\d{1,5}$/.test(String(rawId)) || /^[0-9a-f-]{36}$/i.test(String(rawId)))) {
      return String(rawId);
    }
    return '';
  }
  if (campo_key === 'tipo_via' || campo_key === 'tipo_via_id') {
    const raw = extras.tipo_via_id || extras.tipo_via || '';
    if (raw) return raw;
    const parsed = parseTipoViaCalle(
      String(captacion.calle || extras.nombre_via || extras.calle || '').trim(),
    );
    return parsed.tipoViaCanon || parsed.tipoViaId || '';
  }
  if (campo_key in MAPA_NATIVO) return MAPA_NATIVO[campo_key] ?? '';

  if (campo_key === 'disponible') {
    if (extras.disponible !== undefined && extras.disponible !== null) return extras.disponible;
    if (extras.disponible_desde) return true;
    return '';
  }
  if (campo_key === 'eq_piscina_comun' || campo_key === 'eq_piscina_priv'
      || campo_key === 'eq_aire' || campo_key === 'eq_calefaccion') {
    // Ampliar: si hay tipo (Split / Normal…) el booleano debe verse como Sí aunque un merge dejara false
    if (extras[campo_key] === true || extras[campo_key] === 'true' || extras[campo_key] === 1) return true;
    if (extras[`${campo_key}_id`]) return true;
    if (extras[campo_key] === false || extras[campo_key] === 'false' || extras[campo_key] === 0) return false;
    return '';
  }
  if (campo_key === 'codigo_postal' || campo_key === 'cp') {
    const cp = extras.codigo_postal || extras.cp || extras.domicilio_cp || '';
    if (cp) return cp;
    const pobId = extras.poblacion_id || captacion.poblacion_id || '';
    if (pobId && cpPorPoblacionId(pobId)) return cpPorPoblacionId(pobId);
    const fav = POBLACIONES_FAVORITAS_ALICANTE.find(
      (p) => normLocalidadNombre(p.nombre) === normLocalidadNombre(pobTexto || extras.poblacion || captacion.poblacion),
    );
    return fav ? (cpPorPoblacionId(fav.id) || '') : '';
  }
  if (campo_key === 'provincia_id' || campo_key === 'provincia') {
    const raw = extras.provincia_id ?? extras.provincia ?? extras.domicilio_provincia ?? '';
    if (raw) {
      if (/^\d{1,2}$/.test(String(raw))) return String(raw).padStart(2, '0');
      const byName = PROVINCIAS_ES.find((p) => p.nombre.toLowerCase() === String(raw).toLowerCase());
      if (byName) return campo_key === 'provincia' ? byName.nombre : byName.id;
      return raw;
    }
    const cpDirecto = String(extras.codigo_postal || extras.cp || extras.domicilio_cp || '').replace(/\s/g, '');
    const pobIdCp = extras.poblacion_id || captacion.poblacion_id || '';
    const favCp = POBLACIONES_FAVORITAS_ALICANTE.find(
      (p) => normLocalidadNombre(p.nombre) === normLocalidadNombre(pobTexto || extras.poblacion || captacion.poblacion),
    );
    const cp = cpDirecto
      || String((pobIdCp && cpPorPoblacionId(pobIdCp)) || (favCp ? cpPorPoblacionId(favCp.id) : '') || '').replace(/\s/g, '');
    if (cp.length >= 2 && /^\d/.test(cp)) {
      const id = cp.slice(0, 2).padStart(2, '0');
      if (campo_key === 'provincia') {
        return PROVINCIAS_ES.find((p) => p.id === id)?.nombre || id;
      }
      return id;
    }
    return '';
  }
  // Nº garajes: el móvil escribe en num_plazas_garaje (u aliases)
  if (isGarajePlazasKey(campo_key)) {
    const n = parseNumPlazasGaraje(extras);
    return n == null ? '' : n;
  }

  return extras[campo_key] ?? '';
}

/** Valor mostrado en el modal: captación + datos_extras + ficha propiedad vinculada. */
function resolverValorCampoAgente(campo_key, captacion, propiedad = null) {
  const fromCap = getValorActual(campo_key, captacion);
  if (fromCap !== '' && fromCap != null) return fromCap;
  if (!propiedad || typeof propiedad !== 'object') return fromCap ?? '';
  const MAPA_PROP = {
    nombre_via: propiedad.nombre_via,
    calle: propiedad.nombre_via,
    numero: propiedad.numero,
    tipo_via_id: propiedad.tipo_via_id,
    tipo_via: propiedad.tipo_via,
    codigo_postal: propiedad.codigo_postal,
    cp: propiedad.codigo_postal,
    bloque: propiedad.bloque,
    escalera: propiedad.escalera,
    piso: propiedad.piso,
    puerta: propiedad.puerta,
    poblacion_id: propiedad.poblacion_id,
    provincia_id: propiedad.provincia_id,
  };
  if (Object.prototype.hasOwnProperty.call(MAPA_PROP, campo_key)) {
    const v = MAPA_PROP[campo_key];
    if (v != null && v !== '') return v;
  }
  return fromCap ?? '';
}

export default function ModalCompletarCamposAgente({ isOpen, captacion, onClose, onAvanzado, onOpenFicha, onOpenModalOriginal, titulo }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';
  const { usuario } = useAuth();
  const { puede } = usePermiso();
  const verCompletarGuardar = puede('pl_capt_completar_guardar', 'ver');
  const modCompletarGuardar = puede('pl_capt_completar_guardar', 'modificar');
  const verCompletarAvanzar = puede('pl_capt_completar_avanzar', 'ver');
  const verCompletarFicha = puede('pl_capt_completar_ficha', 'ver');
  const verCompletarFrio = puede('pl_capt_completar_frio', 'ver');
  const verCompletarEliminar = puede('pl_capt_completar_eliminar', 'ver')
    || ['admin', 'director', 'secretaria'].includes(String(usuario?.rol || '').toLowerCase());
  const tituloSinPermisoAgente = 'No tienes permiso para modificar este campo';

  const relacionContacto = useCaptacionRelacionContacto({
    excludeCaptacionId: captacion?.id || null,
  });

  const [camposFaltantes, setCamposFaltantes] = useState([]);
  const [camposCompletos, setCamposCompletos] = useState([]);
  const [total, setTotal] = useState(0);
  const [valores, setValores] = useState({});
  const [agentes, setAgentes] = useState([]);
  const [poblaciones, setPoblaciones] = useState([]);
  const [tiposVia, setTiposVia] = useState([]);
  const [tiposInmueble, setTiposInmueble] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [avanzado, setAvanzado] = useState(false);
  const avanceTimerRef = useRef(null);

  /** Tras auto-avance a Para Revisar (encargo, guardar, etc.). */
  const mostrarAvanceParaRevisar = () => {
    setAvanzado(true);
    setError('');
    setInfoMultimedia('');
    if (avanceTimerRef.current) clearTimeout(avanceTimerRef.current);
    avanceTimerRef.current = setTimeout(async () => {
      try { await onAvanzado?.(); } catch { /* noop */ }
      onClose?.();
    }, 1500);
  };
  const [fotosReportaje, setFotosReportaje] = useState([]);
  const [subiendoFotos, setSubiendoFotos] = useState(false);
  const [lightboxFotoIdx, setLightboxFotoIdx] = useState(null);
  /** 'pantalla' | 'parrilla' — mismo criterio que ModalRevisarCaptacion */
  const [lightboxVistaFoto, setLightboxVistaFoto] = useState('pantalla');
  const [documentosCap, setDocumentosCap] = useState([]);
  const [subiendoDocs, setSubiendoDocs] = useState(false);
  const [lightboxDocIdx, setLightboxDocIdx] = useState(null);
  const [lightboxVistaDoc, setLightboxVistaDoc] = useState('parrilla');
  const [editandoNombreDocId, setEditandoNombreDocId] = useState(null);
  const [nombreDocDraft, setNombreDocDraft] = useState('');
  const [videosReportaje, setVideosReportaje] = useState([]);
  const [subiendoVideos, setSubiendoVideos] = useState(false);
  const [lightboxVideoIdx, setLightboxVideoIdx] = useState(null);
  const [lightboxVistaVideo, setLightboxVistaVideo] = useState('pantalla');
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [infoMultimedia, setInfoMultimedia] = useState('');
  const [esperandoMultimedia, setEsperandoMultimedia] = useState(false);
  const [aceptandoMultimediaIncompleta, setAceptandoMultimediaIncompleta] = useState(false);
  const [accionArchivo, setAccionArchivo] = useState(null);
  const [motivoArchivo, setMotivoArchivo] = useState('');
  const [archivando, setArchivando] = useState(false);
  const inputFotoRef = useRef(null);
  const inputDocRef = useRef(null);
  const inputEncargoRef = useRef(null);
  const [encargoViafirma, setEncargoViafirma] = useState({
    cargando: false,
    setCode: null,
    status: '',
    pendientes: [],
    firmados: [],
  });
  const [subiendoEncargo, setSubiendoEncargo] = useState(false);
  const [extrasFresh, setExtrasFresh] = useState(null);
  const [capFresh, setCapFresh] = useState(null);
  const [propiedadVinculada, setPropiedadVinculada] = useState(null);
  const [modalVinculo, setModalVinculo] = useState(null);
  const [propietariosState, setPropietariosState] = useState(null);
  const [avisoRetroceso, setAvisoRetroceso] = useState(null);
  const dirHydratedRef = useRef(null);

  // ── Drag ────────────────────────────────────────────────────
  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);
  const dragOffsetRef = useRef(null);
  const startDrag = (e) => {
    if (e.target.closest('button,input,textarea,select,label')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = { ox: e.clientX - rect.left, oy: e.clientY - rect.top, w: rect.width, h: rect.height };
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!dragOffsetRef.current) return;
      const { ox, oy, w, h } = dragOffsetRef.current;
      setPos({ x: Math.max(0, Math.min(window.innerWidth - w, ev.clientX - ox)), y: Math.max(0, Math.min(window.innerHeight - h, ev.clientY - oy)) });
    };
    const onUp = () => { dragOffsetRef.current = null; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    if (isOpen && captacion?.id) {
      setPos(null);
      setAvanzado(false);
      setError('');
      setExtrasFresh(null);
      setCapFresh(null);
      setPropiedadVinculada(null);
      setInfoMultimedia('');
      setEsperandoMultimedia(false);
      setAccionArchivo(null);
      setMotivoArchivo('');
      setLightboxFotoIdx(null);
      setLightboxDocIdx(null);
      setLightboxVistaDoc('parrilla');
      setEditandoNombreDocId(null);
      setLightboxVideoIdx(null);
      setModalVinculo(null);
      setPropietariosState(null);
      dirHydratedRef.current = null;
      // Evitar flash de faltantes de la captación anterior (p. ej. Zona de otra ficha).
      setCamposFaltantes([]);
      setCamposCompletos([]);
      setTotal(0);
      setValores({});
      setGuardadoOk(false);
      setAvisoRetroceso(null);
      cargar();
      window.setTimeout(() => {
        cargarFotosReportaje();
        cargarVideosReportaje();
        cargarDocumentos();
      }, 350);
      cargarEstadoEncargoViafirma();

      const tel = captacion.telefono || '';
      const em = captacion.email || '';
      const ejecutarCheckTelefono = () => {
        if (tel || em) {
          void relacionContacto.checkNow(tel, em, { force: true });
        }
      };

      void (async () => {
        try {
          const res = await api.get(`/captaciones/${captacion.id}/ultimo-retroceso-visita`);
          const data = res.data || {};
          const historialId = data.historial_id;
          const userId = usuario?.id;
          const omitirTel = data.omitirAvisoTelefono === true
            || esRetrocesoVisitaReciente(data.retroceso_reciente_at || data.created_at);

          if (
            data.hayRetroceso === true
            && historialId
            && userId
            && !yaVioRetrocesoVisita(userId, historialId)
          ) {
            setAvisoRetroceso({
              historial_id: historialId,
              actor_nombre: data.actor_nombre,
              motivo: data.motivo,
            });
          }

          if (!omitirTel) ejecutarCheckTelefono();
        } catch {
          ejecutarCheckTelefono();
        }
      })();
    }
    // Solo al abrir / cambiar de captación. Si el Pipeline refresca el mismo id,
    // no reiniciar (antes: 2× cargar + spinner largo).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- captacion.id
  }, [isOpen, captacion?.id, usuario?.id]);

  // Refresco silencioso si el padre trae datos nuevos del mismo id (sin spinner).
  useEffect(() => {
    if (!isOpen || !captacion?.id) return;
    if (!capFresh?.id || String(capFresh.id) !== String(captacion.id)) return;
    setCapFresh((prev) => (prev ? { ...prev, ...captacion } : captacion));
    try {
      const ex = (typeof captacion.datos_extras === 'object' && captacion.datos_extras !== null)
        ? captacion.datos_extras
        : (() => { try { return JSON.parse(captacion.datos_extras || '{}'); } catch { return {}; } })();
      if (ex && Object.keys(ex).length) setExtrasFresh((prev) => ({ ...(prev || {}), ...ex }));
    } catch { /* noop */ }
  }, [isOpen, captacion]);

  const parseFirmantesViafirma = (summary) => {
    const recList = Array.isArray(summary?.recipients) ? summary.recipients : [];
    const pendientes = [];
    const firmados = [];
    recList.forEach((r) => {
      const nombre = String(r.name || r.nombre || r.key || 'Firmante').trim();
      const st = String(r.status || r.signingStatus || r.messageStatus || r.state || '').toUpperCase();
      // ViaFirma Documents: tras firmar el recipient suele quedar en RECEIVED
      // (igual que backend isRespondedStatus).
      const ok = /RESPONSED|RECEIVED|COMPLETED|SIGNED|FINISHED|DONE|APPROVED|CLOSED/.test(st);
      if (ok) firmados.push({ nombre, status: st });
      else pendientes.push({ nombre, status: st || 'PENDIENTE' });
    });
    return { pendientes, firmados };
  };

  const cargarEstadoEncargoViafirma = async () => {
    if (!captacion?.id) return;
    setEncargoViafirma((s) => ({ ...s, cargando: true }));
    try {
      const setRes = await api.get(`/viafirma/set-by-captacion/${captacion.id}`).catch(() => null);
      const setCode = setRes?.data?.set_code;
      if (!setCode) {
        setEncargoViafirma({ cargando: false, setCode: null, status: '', pendientes: [], firmados: [] });
        return;
      }
      const stRes = await api.get(`/viafirma/estado/${encodeURIComponent(setCode)}`);
      const summary = stRes.data?.data || {};
      const setSt = String(summary.status || summary.setStatus || '').toUpperCase();
      const { pendientes, firmados } = parseFirmantesViafirma(summary);
      // Solo vaciar pendientes si el SET está realmente cerrado (no con RECEIVED parcial).
      const setCerrado = /^(COMPLETED|FINISHED|DONE|APPROVED|CLOSED)$/.test(setSt)
        || (firmados.length > 0 && pendientes.length === 0 && /RESPONSED|SIGNED|RECEIVED/.test(setSt));
      setEncargoViafirma({
        cargando: false,
        setCode,
        status: setSt,
        pendientes: setCerrado ? [] : pendientes,
        firmados,
      });
    } catch {
      setEncargoViafirma({ cargando: false, setCode: null, status: '', pendientes: [], firmados: [] });
    }
  };

  const puedeForzarValidacionEncargoUi = ['admin', 'administrador', 'director', 'secretaria']
    .includes(String(usuario?.rol || '').toLowerCase());

  const handleSubirEncargoDocumento = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !captacion?.id) return;
    setSubiendoEncargo(true);
    setError('');

    const subirEncargo = async ({ forzar = false } = {}) => {
      const formData = new FormData();
      formData.append('documentos', file);
      formData.append('documentos_meta', JSON.stringify([{
        tipo_codigo: 'nota_encargo',
        nombre_documento: (file.name || 'Encargo firmado').slice(0, 500),
        descripcion_tipo: null,
      }]));
      if (forzar) formData.append('forzar_validacion_encargo', 'true');
      return postCaptacionMultipart(`/captaciones/${captacion.id}/documentos`, formData);
    };

    const aplicarTrasOk = async (body, doc) => {
      const rev = body.encargoRevision;
      const url = doc?.url;
      const avisosPlantilla = Array.isArray(rev?.avisos)
        ? rev.avisos.map((a) => String(a || '').trim()).filter(Boolean)
        : [];
      if (avisosPlantilla.length) {
        mostrarAvisoVitalCasa(
          `El encargo se ha insertado, pero hay diferencias con la plantilla:\n\n${
            avisosPlantilla.map((a) => (a.startsWith('•') ? a : `• ${a}`)).join('\n')
          }`,
          {
            titulo: rev?.forzado ? 'Encargo validado manualmente' : 'Encargo insertado con avisos',
            boton: 'Entendido',
            variante: 'aviso',
            cerrarSoloConBoton: true,
          },
        );
      }

      let avanzo = !!body.autoAvanzado;
      if (!avanzo && url) {
        try {
          await api.patch(`/captaciones/${captacion.id}/datos-extras`, {
            datos: {
              documento_firmado_url: url,
              encargo_documento_manual: true,
              firma_captacion: 'documento-manual',
              encargo_papel_pendiente: false,
              encargo_manual_descripcion: doc?.nombre_documento || file.name,
            },
          });
        } catch (patchErr) {
          if (patchErr.response?.status === 403) {
            const capRes = await api.get(`/captaciones/${captacion.id}`);
            const etapa = capRes.data?.data?.etapa || capRes.data?.etapa;
            if (etapa === 'revisar_cap') avanzo = true;
            else throw patchErr;
          } else {
            throw patchErr;
          }
        }
      }

      if (avanzo) {
        mostrarAvanceParaRevisar();
      } else {
        await cargar(true);
        await cargarEstadoEncargoViafirma();
        setGuardadoOk(true);
      }
    };

    const avisarNoEncargo = (mensaje, rev = null) => {
      let texto = String(mensaje || '').trim()
        || 'Este archivo no es un Encargo de venta ni una Exclusiva de Vitalcasa. No se ha registrado como encargo.';
      if (rev?.conservadoComoGenerico && !/documento genérico/i.test(texto)) {
        texto = `${texto}\n\nEl archivo se ha guardado como documento genérico.`;
      }
      const puedeForzar = !!(rev?.puedeForzarValidacion || puedeForzarValidacionEncargoUi);
      const acciones = [{ label: 'Entendido', primary: true }];
      if (puedeForzar) {
        acciones.unshift({
          label: 'Autorizar como encargo',
          variante: 'validar',
          primary: false,
          onClick: async () => {
            setSubiendoEncargo(true);
            try {
              const res = await subirEncargo({ forzar: true });
              const body = res.data || {};
              const revOk = body.encargoRevision;
              const doc = (body.data || [])[0];
              if (revOk && revOk.ok === false) {
                mostrarAvisoVitalCasa(revOk.mensaje || body.error || 'No se pudo validar', {
                  titulo: 'Encargo no validado',
                  boton: 'Entendido',
                  cerrarSoloConBoton: true,
                });
                return;
              }
              if (!doc?.url) {
                mostrarAvisoVitalCasa('No se recibió el archivo. Prueba de nuevo.', {
                  titulo: 'Encargo no validado',
                  boton: 'Entendido',
                  cerrarSoloConBoton: true,
                });
                return;
              }
              await aplicarTrasOk(body, doc);
            } catch (errF) {
              mostrarAvisoVitalCasa(
                errF.response?.data?.encargoRevision?.mensaje
                  || errF.response?.data?.error
                  || errF.message
                  || 'Error al validar el encargo',
                {
                  titulo: 'Encargo no validado',
                  boton: 'Entendido',
                  cerrarSoloConBoton: true,
                },
              );
            } finally {
              setSubiendoEncargo(false);
            }
          },
        });
      }
      mostrarAvisoVitalCasa(texto, {
        titulo: 'Encargo no validado',
        cerrarSoloConBoton: true,
        acciones,
      });
    };

    try {
      const res = await subirEncargo({ forzar: false });
      const body = res.data || {};
      const rev = body.encargoRevision;
      const doc = (body.data || [])[0];
      if (rev && rev.ok === false) {
        avisarNoEncargo(rev?.mensaje || body.error, rev);
        await cargar(true);
        await cargarDocumentos();
        return;
      }
      if (!doc?.url) {
        avisarNoEncargo('No se recibió el archivo. Prueba de nuevo.');
        return;
      }
      await aplicarTrasOk(body, doc);
    } catch (err) {
      const revErr = err.response?.data?.encargoRevision || null;
      const msg = revErr?.mensaje
        || err.response?.data?.error
        || err.message
        || 'Error subiendo el encargo';
      avisarNoEncargo(msg, revErr);
    } finally {
      setSubiendoEncargo(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !captacion?.id || !esperandoMultimedia) return undefined;
    const poll = setInterval(async () => {
      try {
        const [capRes, fotosRes] = await Promise.all([
          api.get(`/captaciones/${captacion.id}`),
          api.get(`/captaciones/${captacion.id}/fotos-reportaje`),
        ]);
        const etapa = capRes.data?.data?.etapa || capRes.data?.etapa;
        const nFotos = (fotosRes.data?.fotos || []).length;
        setFotosReportaje((fotosRes.data.fotos || []).map((f) => ({
          ...mapFotoCaptacionDesdeApi(f),
          esExistente: true,
        })));
        const extrasMm = capRes.data?.data?.datos_extras || capRes.data?.datos_extras || {};
        const esp = deberFotosCaptacionDisplay(extrasMm, nFotos);
        if (esp > 0 && nFotos >= esp) {
          setInfoMultimedia(`Fotos completas (${nFotos}/${esp}). Comprobando avance…`);
        }
        if (etapa === 'revisar_cap') {
          setAvanzado(true);
          setEsperandoMultimedia(false);
          setTimeout(async () => {
            try { await onAvanzado?.(); } catch {}
            onClose?.();
          }, 1200);
        }
      } catch { /* ignore */ }
    }, 8000);
    return () => clearInterval(poll);
  }, [isOpen, captacion?.id, esperandoMultimedia, onAvanzado, onClose]);

  const cargarFotosReportaje = async () => {
    if (!captacion) return;
    try {
      const [repRes, capRes] = await Promise.all([
        api.get(`/captaciones/${captacion.id}/fotos-reportaje`),
        api.get(`/captaciones/${captacion.id}`).catch(() => null),
      ]);
      let fotos = (repRes.data.fotos || []).map((f) => ({
        ...mapFotoCaptacionDesdeApi(f),
        esExistente: true,
      }));
      // Ampliar: si aún no hay reportaje, mostrar fotos de precaptación / identificación
      if (!fotos.length && capRes) {
        const fresh = capRes.data?.data || capRes.data || {};
        let ident = Array.isArray(fresh.fotos)
          ? fresh.fotos
          : (fresh.fotos
            ? (() => { try { return JSON.parse(fresh.fotos); } catch { return []; } })()
            : []);
        fotos = (ident || []).map((u, i) => {
          const url = typeof u === 'string' ? u : (u?.url_thumb || u?.url || '');
          if (!url) return null;
          return {
            id: `ident-${i}`,
            url,
            url_thumb: typeof u === 'object' ? (u.url_thumb || url) : url,
            esExistente: true,
            soloIdentificacion: true,
          };
        }).filter(Boolean);
      }
      setFotosReportaje(fotos);
    } catch { setFotosReportaje([]); }
  };

  const cargarVideosReportaje = async () => {
    if (!captacion) return;
    try {
      const res = await api.get(`/captaciones/${captacion.id}/videos-reportaje`);
      setVideosReportaje(res.data?.videos || []);
    } catch { setVideosReportaje([]); }
  };

  // Ampliar: si falta poster, reconsultar mientras el backend regenera (mismo espíritu que Para revisar).
  useEffect(() => {
    if (!isOpen || !captacion?.id) return undefined;
    const faltaThumb = videosReportaje.some((v) => v?.id && !v.url_thumb && !String(v.id).startsWith('temp-'));
    if (!faltaThumb) return undefined;
    let n = 0;
    const t = setInterval(() => {
      n += 1;
      cargarVideosReportaje();
      if (n >= 8) clearInterval(t);
    }, 2500);
    return () => clearInterval(t);
  }, [isOpen, captacion?.id, videosReportaje]);

  /** Mismo criterio que la app móvil: por mimetype o, si el navegador no lo informa, por extensión. */
  const esArchivoVideo = (file) => {
    const mt = (file.type || '').toLowerCase();
    if (mt.startsWith('video/')) return true;
    const nombre = (file.name || '').toLowerCase();
    return /\.(mp4|mov|webm|m4v)$/i.test(nombre);
  };

  const handleSubirFotosReportaje = async (e) => {
    const todos = Array.from(e.target.files || []);
    if (!todos.length || !captacion) return;
    e.target.value = '';

    const videos = todos.filter(esArchivoVideo);
    const fotos = todos.filter((f) => !esArchivoVideo(f));

    // En paralelo: poder añadir vídeos mientras suben fotos (y viceversa).
    await Promise.all([
      fotos.length ? subirFotosSeleccionadas(fotos) : Promise.resolve(),
      videos.length ? subirVideosSeleccionados(videos) : Promise.resolve(),
    ]);
  };

  const subirFotosSeleccionadas = async (files) => {
    const previews = await Promise.all(
      files.map(async (f, i) => {
        let thumb = null;
        try {
          thumb = await crearMiniaturaDesdeFile(f);
        } catch {
          thumb = URL.createObjectURL(f);
        }
        return {
          id: `temp-${Date.now()}-${i}`,
          url: URL.createObjectURL(f),
          preview: thumb,
          thumbPreview: thumb,
          esLocal: true,
        };
      }),
    );
    setFotosReportaje(prev => [...prev, ...previews]);
    setSubiendoFotos(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('fotos', f));
      // Directo a Railway: el rewrite de Vercel corta multipart grandes → «Error subiendo fotos».
      const res = await postCaptacionMultipart(`/captaciones/${captacion.id}/fotos-reportaje`, formData);
      const fotosSubidas = (res.data.fotos || []).map((f) => ({
        ...mapFotoCaptacionDesdeApi(f),
        esExistente: true,
      }));
      setFotosReportaje(prev => [
        ...prev.filter(f => !f.esLocal),
        ...fotosSubidas,
      ]);
      // No auto-avanzar aquí: solo «Guardar campos» pasa a Para Revisar.
      cargar(true);
    } catch (err) {
      const detalle = err.response?.data?.error || err.message || '';
      setError(detalle ? `Error subiendo fotos: ${detalle}` : 'Error subiendo fotos');
      setFotosReportaje(prev => prev.filter(f => !f.esLocal));
    } finally {
      setSubiendoFotos(false);
    }
  };

  const subirVideosSeleccionados = async (files) => {
    const previews = files.map((f, i) => ({
      id: `temp-${Date.now()}-${i}`,
      url: URL.createObjectURL(f),
      esLocal: true,
    }));
    setVideosReportaje(prev => [...prev, ...previews]);
    setSubiendoVideos(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('videos', f));
      const res = await postCaptacionMultipart(`/captaciones/${captacion.id}/videos-reportaje`, formData);
      const videosSubidos = res.data?.videos || [];
      setVideosReportaje(prev => [
        ...prev.filter(v => !v.esLocal),
        ...videosSubidos,
      ]);
      cargar(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error subiendo vídeos (usa MP4, MOV o WebM)');
      setVideosReportaje(prev => prev.filter(v => !v.esLocal));
    } finally {
      setSubiendoVideos(false);
    }
  };

  const eliminarFotoReportaje = async (fotoId) => {
    if (String(fotoId || '').startsWith('ident-')) return;
    const idxActual = lightboxFotoIdx;
    setFotosReportaje(prev => {
      const next = prev.filter(f => f.id !== fotoId);
      if (idxActual != null) {
        if (!next.length) setLightboxFotoIdx(null);
        else setLightboxFotoIdx(Math.min(idxActual, next.length - 1));
      }
      return next;
    });
    try {
      await api.delete(`/captaciones/fotos-reportaje/${fotoId}`);
      await cargarFotosReportaje();
      await cargar(true);
    } catch { setError('Error eliminando foto'); }
  };

  const eliminarVideoReportaje = async (videoId) => {
    const idxActual = lightboxVideoIdx;
    setVideosReportaje(prev => {
      const next = prev.filter(v => v.id !== videoId);
      if (idxActual != null) {
        if (!next.length) setLightboxVideoIdx(null);
        else setLightboxVideoIdx(Math.min(idxActual, next.length - 1));
      }
      return next;
    });
    try {
      await api.delete(`/captaciones/videos-reportaje/${videoId}`);
      await cargarVideosReportaje();
      await cargar(true);
    } catch { setError('Error eliminando vídeo'); }
  };

  const cargarDocumentos = async () => {
    if (!captacion) return;
    try {
      const res = await api.get(`/captaciones/${captacion.id}/documentos`);
      const list = res.data?.data ?? res.data?.documentos;
      setDocumentosCap(Array.isArray(list) ? list : []);
    } catch { setDocumentosCap([]); }
  };

  const handleSubirDocumentos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !captacion) return;
    e.target.value = '';
    setSubiendoDocs(true);
    setError('');
    try {
      const faltaNotaSimpleDoc = camposFaltantes.some((c) => esCampoNotaSimpleDoc(c.campo_key));
      const formData = new FormData();
      const meta = files.map((f) => ({
        tipo_codigo: faltaNotaSimpleDoc ? 'nota_simple_informativa' : 'documento_generico',
        nombre_documento: (f.name || 'Documento').slice(0, 500),
        descripcion_tipo: faltaNotaSimpleDoc
          ? 'Nota simple o escritura (m² construidos declarados)'
          : null,
      }));
      files.forEach((f) => formData.append('documentos', f));
      formData.append('documentos_meta', JSON.stringify(meta));
      const res = await postCaptacionMultipart(`/captaciones/${captacion.id}/documentos`, formData);
      const nuevos = res.data?.data || [];
      setDocumentosCap((prev) => [...prev, ...nuevos]);
      // Encargo/docs: no cerrar ni avanzar; el agente pulsa Guardar cuando termine.
      await cargarDocumentos();
      await cargar(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error subiendo documentos');
    } finally {
      setSubiendoDocs(false);
    }
  };

  const eliminarDocumentoCap = async (docId) => {
    const idxActual = lightboxDocIdx;
    setDocumentosCap((prev) => {
      const next = prev.filter((d) => d.id !== docId);
      if (idxActual != null) {
        if (!next.length) setLightboxDocIdx(null);
        else setLightboxDocIdx(Math.min(idxActual, next.length - 1));
      }
      return next;
    });
    try {
      await api.delete(`/captaciones/documentos/${docId}`);
      await cargarDocumentos();
      await cargar(true);
    } catch { setError('Error eliminando documento'); }
  };

  const iniciarEdicionNombreDoc = (doc) => {
    if (!doc?.id) return;
    setEditandoNombreDocId(doc.id);
    setNombreDocDraft(doc.nombre_documento || doc.nombre_original || '');
  };

  const guardarNombreDocumento = async (doc) => {
    const nombre = String(nombreDocDraft || '').trim().slice(0, 500);
    setEditandoNombreDocId(null);
    if (!doc?.id || !nombre) return;
    if (nombre === (doc.nombre_documento || doc.nombre_original || '')) return;
    setDocumentosCap((prev) => prev.map((d) => (d.id === doc.id ? { ...d, nombre_documento: nombre } : d)));
    try {
      await api.patch(`/captaciones/documentos/${doc.id}`, { nombre_documento: nombre });
    } catch {
      setError('Error guardando el nombre del documento');
      await cargarDocumentos();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    api.get('/agentes')
      .then((res) => setAgentes(res.data?.data || []))
      .catch(() => setAgentes([]));
    api.get('/auxiliares/tipo-via')
      .then((res) => setTiposVia(res.data?.data || []))
      .catch(() => setTiposVia([]));
    api.get('/auxiliares/tipo-inmueble')
      .then((res) => setTiposInmueble(res.data?.data || []))
      .catch(() => setTiposInmueble([]));
  }, [isOpen]);

  const provinciaIdActual = (() => {
    const raw = valores.provincia_id ?? valores.provincia ?? '';
    if (!raw) return '';
    if (/^\d{1,2}$/.test(String(raw))) return String(raw).padStart(2, '0');
    const prov = PROVINCIAS_ES.find((p) => p.nombre.toLowerCase() === String(raw).toLowerCase());
    return prov?.id || '';
  })();

  useEffect(() => {
    if (!isOpen || !provinciaIdActual) {
      setPoblaciones([]);
      return;
    }
    api.get(`/auxiliares/poblaciones/${provinciaIdActual}`)
      .then((res) => setPoblaciones(res.data?.data || []))
      .catch(() => setPoblaciones([]));
  }, [isOpen, provinciaIdActual]);

  const aplicarDireccionDesdePlaces = async (parts) => {
    if (!parts) return;
    const calleRaw = String(parts.calle || '').trim();
    const numeroRaw = String(parts.numero || '').trim();
    const parsedVia = parseTipoViaCalle(
      [calleRaw, numeroRaw].filter(Boolean).join(numeroRaw && !calleRaw.includes(numeroRaw) ? `, ${numeroRaw}` : ''),
      tiposVia,
    );
    const calle = parsedVia.nombreVia || calleRaw;
    const numero = parsedVia.numero || numeroRaw;
    let tipoViaId = parsedVia.tipoViaId || '';
    if (!tipoViaId && parsedVia.tipoViaCanon) {
      tipoViaId = normalizeAuxSelectValue(parsedVia.tipoViaCanon, tiposVia);
    }
    const cp = String(parts.codigo_postal || '').trim();
    const pobNombre = resolverPoblacionPlaces(parts);
    const provId = resolverProvinciaId(parts.provincia, PROVINCIAS_ES);

    setValores((prev) => {
      const next = { ...prev };
      if (calle) {
        if ('nombre_via' in prev || camposFaltantes.some((c) => c.campo_key === 'nombre_via')) {
          next.nombre_via = calle;
        }
        if ('calle' in prev || camposFaltantes.some((c) => c.campo_key === 'calle')) {
          next.calle = calle;
        }
        if (!('nombre_via' in next) && !('calle' in next)) next.nombre_via = calle;
      }
      if (numero) next.numero = numero;
      if (tipoViaId) {
        Object.assign(next, buildAuxExtrasPatch('tipo_via_id', tipoViaId));
        const tv = tiposVia.find((t) => String(t.id) === String(tipoViaId));
        if (tv?.nombre) next.tipo_via = tv.nombre;
        else if (parsedVia.tipoViaCanon) next.tipo_via = parsedVia.tipoViaCanon;
      } else if (parsedVia.tipoViaCanon) {
        // Catálogo aún vacío o sin match: guardar nombre canónico para hidratar luego
        next.tipo_via = parsedVia.tipoViaCanon;
      }
      if (cp) next.codigo_postal = cp;
      if (provId) {
        next.provincia_id = provId;
        next.provincia = PROVINCIAS_ES.find((p) => p.id === provId)?.nombre || parts.provincia || prev.provincia;
      } else if (parts.provincia) {
        next.provincia = String(parts.provincia).trim();
      }
      if (parts.latitud != null) next.latitud = parts.latitud;
      if (parts.longitud != null) next.longitud = parts.longitud;
      return next;
    });

    const pid = provId || provinciaIdActual;
    if (!pid || !pobNombre) return;
    try {
      const res = await api.get(`/auxiliares/poblaciones/${pid}`);
      const rows = res.data?.data || [];
      setPoblaciones(rows);
      const pobId = resolverPoblacionIdEnLista(pobNombre, rows);
      setValores((prev) => ({
        ...prev,
        ...(pobId ? { poblacion_id: pobId, poblacion: pobNombre } : { poblacion: pobNombre }),
        ...((!prev.codigo_postal && pobId && cpPorPoblacionId(pobId))
          ? { codigo_postal: cpPorPoblacionId(pobId) }
          : {}),
      }));
    } catch {
      setValores((prev) => ({ ...prev, poblacion: pobNombre }));
    }
  };

  // Hidratar dirección ya conocida (calle/población de precaptación) en campos
  // estructurados: tipo de vía, provincia, población_id, CP.
  useEffect(() => {
    if (!isOpen || !capFresh?.id || !tiposVia.length) return;
    if (cargando) return;

    const extras = (typeof capFresh.datos_extras === 'object' && capFresh.datos_extras !== null)
      ? capFresh.datos_extras
      : (() => { try { return JSON.parse(capFresh.datos_extras || '{}'); } catch { return {}; } })();

    const calleSeed = String(
      valores.nombre_via || valores.calle || capFresh.calle || extras.nombre_via || extras.calle || '',
    ).trim();
    // El <select> lee tipo_via_id; un nombre en tipo_via no basta para mostrarlo
    const necesitaTipoVia = !normalizeAuxSelectValue(
      valores.tipo_via_id || extras.tipo_via_id || '',
      tiposVia,
    );
    const prefijoViaRe = /^(carrer|calle|c\/|cl\.?|avinguda|avenida|avda\.?|av\.?|passeig|paseo|plaça|plaza)\b/i;
    const puedeSacarTipo = prefijoViaRe.test(calleSeed)
      || (!!valores.tipo_via && !valores.tipo_via_id);
    const calleConPrefijo = prefijoViaRe.test(calleSeed) || /,\s*\d{1,5}\s*$/.test(calleSeed);

    // Tras un intento, solo reintentar si la vía sigue sucia o falta tipo y el texto trae prefijo
    if (dirHydratedRef.current === String(capFresh.id)) {
      if (!puedeSacarTipo && !calleConPrefijo) return;
      if (!necesitaTipoVia && !calleConPrefijo) return;
    }

    let cancelled = false;
    (async () => {
      const patch = {};
      const numeroRaw = String(valores.numero || capFresh.numero || extras.numero || '').trim();
      if (calleSeed) {
        const parsed = parseTipoViaCalle(
          numeroRaw && !calleSeed.includes(numeroRaw) ? `${calleSeed}, ${numeroRaw}` : calleSeed,
          tiposVia,
        );
        let tipoId = parsed.tipoViaId || '';
        if (!tipoId && parsed.tipoViaCanon) {
          tipoId = normalizeAuxSelectValue(parsed.tipoViaCanon, tiposVia);
        }
        if (!tipoId && (valores.tipo_via || extras.tipo_via)) {
          tipoId = normalizeAuxSelectValue(valores.tipo_via || extras.tipo_via, tiposVia);
        }
        if (tipoId && necesitaTipoVia) {
          Object.assign(patch, buildAuxExtrasPatch('tipo_via_id', tipoId));
          const tv = tiposVia.find((t) => String(t.id) === String(tipoId));
          if (tv?.nombre) patch.tipo_via = tv.nombre;
          else if (parsed.tipoViaCanon) patch.tipo_via = parsed.tipoViaCanon;
        }
        // Forzar limpieza de "Carrer de Diana, 25" → nombre de vía sin prefijo/nº
        if (parsed.nombreVia && (calleConPrefijo || !valores.nombre_via)) {
          patch.nombre_via = parsed.nombreVia;
          if ('calle' in valores || valores.calle) patch.calle = parsed.nombreVia;
        }
        if (parsed.numero && !numeroRaw) patch.numero = parsed.numero;
      }

      const pobNombre = String(
        valores.poblacion
        || capFresh.poblacion
        || extras.poblacion
        || extras.domicilio_poblacion
        || extras.poblacion_nombre
        || '',
      ).trim();

      let provId = '';
      const provRaw = valores.provincia_id || valores.provincia || extras.provincia_id || extras.provincia || extras.domicilio_provincia || '';
      if (provRaw) {
        if (/^\d{1,2}$/.test(String(provRaw))) provId = String(provRaw).padStart(2, '0');
        else provId = resolverProvinciaId(provRaw, PROVINCIAS_ES);
      }
      if (!provId) {
        const cp = String(valores.codigo_postal || extras.codigo_postal || extras.cp || extras.domicilio_cp || '').replace(/\s/g, '');
        if (cp.length >= 2 && /^\d/.test(cp)) provId = cp.slice(0, 2);
      }

      if (!provId && pobNombre) {
        const fav = POBLACIONES_FAVORITAS_ALICANTE.find(
          (p) => normLocalidadNombre(p.nombre) === normLocalidadNombre(pobNombre),
        );
        if (fav) provId = '03';
      }

      if (provId && !(valores.provincia_id || extras.provincia_id)) {
        patch.provincia_id = provId;
        patch.provincia = PROVINCIAS_ES.find((p) => p.id === provId)?.nombre || valores.provincia || extras.provincia || '';
      }

      const cpExistente = String(valores.codigo_postal || extras.codigo_postal || extras.cp || extras.domicilio_cp || '').trim();
      if (cpExistente && !valores.codigo_postal) patch.codigo_postal = cpExistente;

      if (pobNombre && !cancelled) {
        const tryProvs = provId ? [provId] : ['03', '46', '12'];
        for (const pid of tryProvs) {
          try {
            const res = await api.get(`/auxiliares/poblaciones/${pid}`);
            if (cancelled) return;
            const rows = res.data?.data || [];
            const pobId = resolverPoblacionIdEnLista(pobNombre, rows)
              || (() => {
                const fav = POBLACIONES_FAVORITAS_ALICANTE.find(
                  (p) => normLocalidadNombre(p.nombre) === normLocalidadNombre(pobNombre),
                );
                return fav?.id || '';
              })();
            if (pobId) {
              setPoblaciones(rows);
              if (!valores.poblacion_id) patch.poblacion_id = pobId;
              patch.poblacion = pobNombre;
              if (!provId) {
                patch.provincia_id = pid;
                patch.provincia = PROVINCIAS_ES.find((p) => p.id === pid)?.nombre || '';
              }
              if (!cpExistente && !valores.codigo_postal) {
                const cpAuto = cpPorPoblacionId(pobId);
                if (cpAuto) patch.codigo_postal = cpAuto;
              }
              break;
            }
          } catch { /* siguiente provincia */ }
        }
        if (!patch.poblacion && pobNombre) patch.poblacion = pobNombre;
      }

      dirHydratedRef.current = String(capFresh.id);
      if (cancelled || !Object.keys(patch).length) return;
      setValores((prev) => {
        const next = { ...prev };
        Object.entries(patch).forEach(([k, v]) => {
          if (v == null || v === '') return;
          if (k === 'tipo_via_id' || k === 'tipo_via') {
            // Forzar siempre: el select puede tener un UUID inválido legado
            next[k] = v;
            return;
          }
          if (k === 'nombre_via' || k === 'calle') {
            const cur = String(next[k] || '');
            if (!cur || prefijoViaRe.test(cur) || /,\s*\d/.test(cur)) next[k] = v;
            return;
          }
          if (next[k] != null && next[k] !== '') return;
          next[k] = v;
        });
        return next;
      });
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    capFresh?.id,
    tiposVia,
    cargando,
    valores.nombre_via,
    valores.calle,
    valores.tipo_via_id,
    valores.codigo_postal,
    valores.poblacion,
  ]);

  const auxCampoKeys = useMemo(
    () => {
      const keys = [...camposFaltantes, ...camposCompletos].map((c) => c.campo_key).filter(Boolean);
      Object.values(TOGGLE_SELECTOR_CFG).forEach((cfg) => {
        if (cfg.detailKey) keys.push(cfg.detailKey);
      });
      return keys;
    },
    [camposFaltantes, camposCompletos]
  );
  const auxLists = useAuxListsForCampos(isOpen, auxCampoKeys);

  const cargar = async (preserveValues = false) => {
    if (!preserveValues) setCargando(true);
    try {
      const [camposRes, capRes] = await Promise.all([
        api.get(`/campos-obligatorios/verificar-captacion/${captacion.id}?rol=agente`),
        api.get(`/captaciones/${captacion.id}`),
      ]);
      const freshCap = capRes.data?.data || capRes.data || captacion;
      const extrasParsed = (typeof freshCap.datos_extras === 'object' && freshCap.datos_extras !== null)
        ? freshCap.datos_extras
        : (() => { try { return JSON.parse(freshCap.datos_extras || '{}'); } catch { return {}; } })();
      // No bloquear el spinner con GET /propiedades (puede ser pesado).
      // Hidratar valores con propRow=null; la prop llega en segundo plano.
      setPropiedadVinculada(null);
      setExtrasFresh(extrasParsed);
      setCapFresh(freshCap);
      if (freshCap.etapa === 'revisar_cap') {
        mostrarAvanceParaRevisar();
        return;
      }
      setPropietariosState(hidratarPropietariosDesdeExtras(extrasParsed, freshCap));
      const faltantes = camposRes.data.camposFaltantes || [];
      const completos = camposRes.data.camposCompletos || [];
      setCamposFaltantes(faltantes);
      setCamposCompletos(completos);
      setTotal(camposRes.data.total || faltantes.length + completos.length);

      const aplicarValores = (propRow) => {
        if (preserveValues) return;
        const vals = {};
        faltantes.forEach(f => {
          const actual = resolverValorCampoAgente(f.campo_key, freshCap, propRow);
          const tipo = getTipo(f.campo_key, f.tipo_campo);
          if (tipo === 'boolean') {
            if (actual === true || actual === 'true' || actual === 1 || actual === '1') vals[f.campo_key] = true;
            else if (actual === false || actual === 'false' || actual === 0 || actual === '0') vals[f.campo_key] = false;
            else vals[f.campo_key] = null;
          } else if (f.campo_key === 'disponible_desde' && !actual) {
            vals[f.campo_key] = hoyISO();
          } else {
            vals[f.campo_key] = actual;
          }
        });
        [
          'num_trastero', 'num_garaje', 'vpo_fecha_fin', 'vpo_ano_fin',
          'eq_aire_id', 'eq_calefaccion_id', 'eq_piscina_priv_id', 'eq_piscina_comun_id',
          'cocina_amueblada_obs', 'armarios_obs', 'garaje_tipos', 'garajes_plazas_detalle',
          'num_plazas_garaje', 'ub_afectacion_costas', 'ub_dpmt', 'ub_serv_transito', 'ub_serv_proteccion',
          'ub_afectacion_costas_obs', 'ub_dpmt_obs', 'ub_serv_transito_obs', 'ub_serv_proteccion_obs',
          'eq_garaje', 'eq_trastero', 'eq_aire', 'eq_calefaccion', 'vpo',
          'nombre_via', 'calle', 'numero', 'tipo_via_id', 'tipo_via',
          'bloque', 'escalera', 'piso', 'puerta',
          'codigo_postal', 'provincia_id', 'provincia', 'poblacion_id', 'poblacion',
        ].forEach((k) => {
          if (vals[k] !== undefined) return;
          const actual = resolverValorCampoAgente(k, freshCap, propRow);
          if (actual !== undefined && actual !== null && actual !== '') vals[k] = actual;
        });
        ['eq_aire', 'eq_calefaccion', 'eq_piscina_priv', 'eq_piscina_comun'].forEach((boolK) => {
          const idK = `${boolK}_id`;
          if (vals[idK] && vals[boolK] !== true) vals[boolK] = true;
        });
        setValores(vals);
      };

      aplicarValores(null);

      if (freshCap.propiedad_id) {
        void (async () => {
          try {
            const pr = await api.get(`/propiedades/${freshCap.propiedad_id}`);
            const propRow = pr.data?.data || pr.data || null;
            setPropiedadVinculada(propRow);
            aplicarValores(propRow);
          } catch {
            setPropiedadVinculada(null);
          }
        })();
      }
    } catch (e) {
      setError('Error cargando campos obligatorios');
      setCamposFaltantes([]);
      setCamposCompletos([]);
    } finally {
      setCargando(false);
    }
  };

  // Variante de cargar que retorna los datos para poder reaccionar al resultado
  const cargarYDevolver = async () => {
    try {
      const [camposRes, capRes] = await Promise.all([
        api.get(`/campos-obligatorios/verificar-captacion/${captacion.id}?rol=agente`),
        api.get(`/captaciones/${captacion.id}`),
      ]);
      const freshCap = capRes.data?.data || capRes.data || captacion;
      const faltantes = camposRes.data.camposFaltantes || [];
      const completos = camposRes.data.camposCompletos || [];
      setCamposFaltantes(faltantes);
      setCamposCompletos(completos);
      setTotal(camposRes.data.total || faltantes.length + completos.length);
      return { camposFaltantes: faltantes, camposCompletos: completos, freshCap };
    } catch {
      return null;
    }
  };

  const extrasCap = extrasFresh || ((typeof captacion?.datos_extras === 'object' && captacion?.datos_extras !== null)
    ? captacion.datos_extras
    : (() => { try { return JSON.parse(captacion?.datos_extras || '{}'); } catch { return {}; } })());
  const exclusivaRaw = (valores.exclusiva !== undefined && valores.exclusiva !== null && valores.exclusiva !== '')
    ? valores.exclusiva
    : extrasCap.exclusiva;
  const exclusivaActiva = exclusivaRaw === true || exclusivaRaw === 'true' || exclusivaRaw === 1 || exclusivaRaw === '1';

  const subidaFotosPendienteRaw = extrasCap.subida_multimedia_pendiente === true;
  const fotosSubidas = fotosReportaje.filter((f) => {
    const id = String(f.id || '');
    return id && !id.startsWith('temp-') && !id.startsWith('ident-') && !f.soloIdentificacion;
  }).length;
  const videosSubidos = videosReportaje.filter((v) => v.id && !String(v.id).startsWith('temp-')).length;
  const fotosEsperadas = deberFotosCaptacionDisplay(extrasCap, fotosSubidas);
  const videosEsperados = deberVideosCaptacionDisplay(extrasCap, videosSubidos);
  // No mostrar «Subiendo» si el conteo ya cuadra (flag pegado).
  const subidaFotosPendiente = subidaFotosPendienteRaw && !(
    (fotosEsperadas > 0 && fotosSubidas >= fotosEsperadas && videosSubidos >= videosEsperados)
    || (fotosEsperadas <= 0 && fotosSubidas > 0 && videosEsperados <= 0)
  );
  const faltaFotosMm = fotosEsperadas > 0 && fotosSubidas < fotosEsperadas;
  const faltaVideosMm = videosEsperados > 0 && videosSubidos < videosEsperados;
  const faltanFotosNum = faltaFotosMm ? Math.max(0, fotosEsperadas - fotosSubidas) : 0;
  const faltanVideosNum = faltaVideosMm ? Math.max(0, videosEsperados - videosSubidos) : 0;
  // Subida viva: trozo reciente o spinner local. Si no, el hueco está parado
  // (Josefa Xeresa: 30/31·0/1 sin wake) → ofrecer re-subir o enviar a Para Revisar.
  const trozoMmAt = extrasCap.multimedia_ultimo_trozo_at
    ? new Date(extrasCap.multimedia_ultimo_trozo_at).getTime()
    : 0;
  const subiendoAhoraMm = trozoMmAt > 0 && Date.now() - trozoMmAt < 15 * 60 * 1000;
  const multimediaHuecoParado = (faltaFotosMm || faltaVideosMm)
    && !subiendoFotos
    && !subiendoVideos
    && !subiendoAhoraMm
    && extrasCap.agente_acepto_multimedia_incompleta !== true;
  const textoHuecoMultimedia = (() => {
    const partes = [];
    if (faltanFotosNum > 0) {
      partes.push(`${faltanFotosNum} foto${faltanFotosNum === 1 ? '' : 's'}`);
    }
    if (faltanVideosNum > 0) {
      partes.push(`${faltanVideosNum} vídeo${faltanVideosNum === 1 ? '' : 's'}`);
    }
    if (!partes.length) return '';
    return partes.join(' y ');
  })();
  // Ampliar: no confiar solo en subida_multimedia_pendiente / estado «lista».
  // Si el deber (selección/meta) supera lo subido, el Avanzar verde mentía y el
  // backend rechazaba (Irina Barkova: estado_multimedia=lista con 4/5 vídeos).
  const avanceBloqueadoMultimedia = !!(
    subidaFotosPendiente
    || subiendoFotos
    || subiendoVideos
    || esperandoMultimedia
    || faltaFotosMm
    || faltaVideosMm
  );
  const textoEsperandoAvance = faltaFotosMm && faltaVideosMm
    ? `⏳ Esperando ${fotosSubidas}/${fotosEsperadas} · ${videosSubidos}/${videosEsperados}`
    : faltaVideosMm
      ? `⏳ Esperando vídeos (${videosSubidos}/${videosEsperados})`
      : faltaFotosMm
        ? `⏳ Esperando fotos (${fotosSubidas}/${fotosEsperadas})`
        : (videosEsperados > 0 && fotosSubidas >= fotosEsperadas
          ? '⏳ Esperando vídeos…'
          : '⏳ Esperando fotos…');
  const faltaParaAvance = camposFaltantes.filter(
    (c) => !(CAMPOS_EXCLUSIVA_DEP.includes(c.campo_key) && !exclusivaActiva),
  ).length;

  /** Campos O de propietario (Config APP, filtrados por tipo en verificar-captacion). */
  const camposPropietarioObl = useMemo(() => (
    [...camposFaltantes, ...camposCompletos].filter((c) => esCampoPropietarioConfig(c.campo_key))
  ), [camposFaltantes, camposCompletos]);

  // No auto-avanzar a Para Revisar al completar campos: solo al pulsar «Guardar campos».

  const handleEnviarParaRevisarSinPiezasFaltantes = async () => {
    if (!captacion?.id || aceptandoMultimediaIncompleta) return;
    if (fotosSubidas <= 0) {
      setError('Hace falta al menos una foto del inmueble. Súbela aquí o desde el móvil.');
      return;
    }
    const ok = window.confirm(
      `Han faltado ${textoHuecoMultimedia || 'piezas'} por subir.\n\n`
      + 'Si ya no las tienes en el móvil, puedes enviar la captación a Para Revisar con lo que sí ha llegado.\n\n'
      + '¿Enviar a Para Revisar sin esas piezas?',
    );
    if (!ok) return;
    setAceptandoMultimediaIncompleta(true);
    setError('');
    try {
      const res = await api.post(
        `/captaciones/${captacion.id}/aceptar-multimedia-incompleta`,
        { avanzar: true },
      );
      if (res.data?.avanzado) {
        setAvanzado(true);
        setInfoMultimedia(
          `Enviada a Para Revisar sin ${textoHuecoMultimedia || 'las piezas faltantes'}.`,
        );
        setTimeout(async () => {
          try { await onAvanzado?.(); } catch { /* noop */ }
          onClose?.();
        }, 1200);
        return;
      }
      // Aceptado pero no avanzó (campos): refrescar y avisar.
      await cargar(true);
      setInfoMultimedia(
        res.data?.error
          || 'Hueco de multimedia aceptado. Completa los campos y vuelve a avanzar.',
      );
    } catch (e) {
      const body = e.response?.data;
      setError(
        body?.error
        || (Array.isArray(body?.camposFaltantes) && body.camposFaltantes.length
          ? `Faltan campos: ${body.camposFaltantes.map((c) => c.campo_label || c.campo_key).join(', ')}`
          : null)
        || 'No se pudo enviar a Para Revisar sin las piezas faltantes.',
      );
      if (body?.aceptado) {
        try { await cargar(true); } catch { /* noop */ }
      }
    } finally {
      setAceptandoMultimediaIncompleta(false);
    }
  };

  const handleArchivar = async () => {
    if (!captacion) return;
    setArchivando(true);
    setError('');
    try {
      const etapaDestino = accionArchivo === 'frio' ? 'fria' : 'eliminado';
      await api.patch(`/captaciones/${captacion.id}/etapa`, {
        etapa: etapaDestino,
        motivo: motivoArchivo || null,
        historial_data: {
          tipo: 'cambio_etapa',
          contenido: etapaDestino === 'fria'
            ? (motivoArchivo || 'Enviada a Fríos desde Visita concertada')
            : (motivoArchivo || 'Eliminada desde Visita concertada'),
          metadata: { etapa_origen: captacion.etapa || 'visita_cap', motivo: motivoArchivo || null },
        },
      });
      setAccionArchivo(null);
      setMotivoArchivo('');
      try { await onAvanzado?.(); } catch {}
      onClose?.();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al archivar';
      setError(msg);
    } finally {
      setArchivando(false);
    }
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setError('');
    setInfoMultimedia('');
    setGuardadoOk(false);
    const etapaViva = capFresh?.etapa || captacion?.etapa;
    if (etapaViva === 'revisar_cap') {
      mostrarAvanceParaRevisar();
      setGuardando(false);
      return;
    }
    const faltaSiNo = camposFaltantes.find((f) => {
      if (f.campo_key === 'firma_captacion') return false;
      if (CAMPOS_EXCLUSIVA_DEP.includes(f.campo_key) && !exclusivaActiva) return false;
      if (getTipo(f.campo_key, f.tipo_campo) !== 'boolean') return false;
      const v = valores[f.campo_key];
      if (v === true || v === false) return false;
      if (v === 'true' || v === 'false') return false;
      return true;
    });
    if (faltaSiNo) {
      setError(`Selecciona Sí o No: ${faltaSiNo.campo_label}`);
      setGuardando(false);
      return;
    }
    const faltaPlazas = camposFaltantes.find((f) => {
      if (!isGarajePlazasKey(f.campo_key)) return false;
      const v = valores[f.campo_key];
      if (v === 0 || v === '0') return false;
      if (v === null || v === undefined || v === '') return true;
      const n = parseInt(String(v), 10);
      return Number.isNaN(n) || n < 0;
    });
    if (faltaPlazas) {
      setError(`Indica el nº de plazas de garaje (0 / 1 / 2+): ${faltaPlazas.campo_label}`);
      setGuardando(false);
      return;
    }
    const faltaAgenteAhora = camposFaltantes.filter(
      (c) => !(CAMPOS_EXCLUSIVA_DEP.includes(c.campo_key) && !exclusivaActiva),
    ).length;
    if (faltaAgenteAhora === 0 && (
      subidaFotosPendiente
      || subiendoFotos
      || subiendoVideos
      || esperandoMultimedia
    )) {
      setError('Espera a que terminen de subirse las fotos y vídeos del móvil. No se puede pasar a Para Revisar todavía.');
      setGuardando(false);
      return;
    }
    if (propietariosState) {
      const telErr = validarTelefonoPrincipalState(propietariosState);
      if (telErr) {
        setError(telErr);
        setGuardando(false);
        return;
      }
    }
    try {
      const payload = {};
      Object.entries(valores).forEach(([k, v]) => {
        if (CAMPOS_EXCLUSIVA_DEP.includes(k) && !exclusivaActiva) return;
        if (isMoneyField(k)) {
          const money = parseMoney(v);
          if (money !== null) payload[k] = money;
          return;
        }
        // null = desmarcado a propósito (Sí/No); hay que persistirlo
        if (v === null) {
          payload[k] = null;
          return;
        }
        // 0 es valor válido (p. ej. 0 plazas de garaje)
        if (v === 0 || v === '0') {
          payload[k] = typeof v === 'string' ? 0 : v;
          return;
        }
        if (v !== '' && v !== undefined) payload[k] = v;
      });
      // Sincronizar eq_garaje si se guardó nº plazas
      for (const k of Object.keys(payload)) {
        if (!isGarajePlazasKey(k)) continue;
        Object.assign(payload, buildGarajePlazasPatch(payload[k]));
        break;
      }
      if (propietariosState) {
        Object.assign(payload, patchPropietariosParaExtras(propietariosState));
      }
      // Ampliar: no auto-avanzar si el encargo sigue en «pendiente» (ViaFirma sin firmar),
      // aunque el listado de faltantes se hubiera filtrado mal en UI.
      let falta = camposFaltantes.length;
      const firmaVis = valores?.firma_captacion != null && String(valores.firma_captacion).trim() !== ''
        ? valores.firma_captacion
        : extrasCap?.firma_captacion;
      const papelPend = extrasCap?.encargo_papel_pendiente === true
        || extrasCap?.encargo_papel_pendiente === 'true';
      if (papelPend || (firmaVis != null && String(firmaVis).trim() !== '' && !valorFirmaCaptacionEsRelleno(firmaVis))) {
        falta = Math.max(falta, 1);
      }
      if (!extrasCap?.documento_firmado_url && !extrasCap?.encargo_documento_manual
          && !valorFirmaCaptacionEsRelleno(firmaVis)
          && [...camposFaltantes, ...camposCompletos].some((c) => c.campo_key === 'firma_captacion')) {
        falta = Math.max(falta, 1);
      }
      const res = await api.patch(`/captaciones/${captacion.id}/datos-extras`, {
        datos: payload,
        solo_borrador: falta > 0,
      });
      if (res.data.autoAvanzado) {
        setAvanzado(true);
        setTimeout(async () => {
          try { await onAvanzado?.(); } catch {}
          onClose?.();
        }, 1500);
      } else if (res.data.multimediaPendiente) {
        setGuardadoOk(true);
        setEsperandoMultimedia(true);
        setInfoMultimedia(
          res.data.mensaje ||
            'Campos guardados. Pasará a Para Revisar cuando terminen de subirse las fotos.',
        );
        try { await onAvanzado?.(); } catch {}
        onClose?.();
      } else {
        // Refrescar campos para ver si ya quedan 0 faltantes
        setGuardadoOk(true);
        const fresh = await cargarYDevolver();
        const nFotosLocal = fotosReportaje.filter((f) => f.id && !String(f.id).startsWith('temp-')).length;
        if (fresh?.camposFaltantes?.length === 0 && nFotosLocal > 0 && !avanceBloqueadoMultimedia) {
          try {
            await api.patch(`/captaciones/${captacion.id}/etapa`, { etapa: 'revisar_cap' });
            setAvanzado(true);
            setTimeout(async () => {
              try { await onAvanzado?.(); } catch {}
              onClose?.();
            }, 1500);
          } catch (e) {
            const body = e.response?.data;
            if (body?.multimediaPendiente) {
              setEsperandoMultimedia(true);
              setInfoMultimedia(
                body.error ||
                  'Campos guardados. Pasará a Para Revisar cuando terminen de subirse las fotos.',
              );
              setGuardadoOk(true);
              try { await onAvanzado?.(); } catch {}
              onClose?.();
              return;
            }
            const msg =
              body?.error ||
              (Array.isArray(body?.camposFaltantes) && body.camposFaltantes.length
                ? `Faltan campos obligatorios: ${body.camposFaltantes.map((c) => c.campo_label || c.campo_key).join(', ')}`
                : null) ||
              'No se pudo pasar a Para Revisar. Revisa los campos o vuelve a guardar.';
            setError(msg);
            setGuardadoOk(false);
            await cargar(true);
          }
        } else {
          // Borrador guardado (aún faltan campos): cerrar y volver al pipeline.
          try { await onAvanzado?.(); } catch {}
          onClose?.();
        }
      }
    } catch (e) {
      if (e.response?.data?.multimediaPendiente) {
        setGuardadoOk(true);
        setEsperandoMultimedia(true);
        setInfoMultimedia(
          e.response?.data?.error ||
            'Campos guardados. Pasará a Para Revisar cuando terminen de subirse las fotos.',
        );
        try { await onAvanzado?.(); } catch {}
        onClose?.();
      } else if (e.response?.status === 403) {
        try {
          const capRes = await api.get(`/captaciones/${captacion.id}`);
          const etapa = capRes.data?.data?.etapa || capRes.data?.etapa;
          if (etapa === 'revisar_cap') {
            mostrarAvanceParaRevisar();
          } else {
            setError(e.response?.data?.error || 'Error guardando campos');
          }
        } catch {
          setError(e.response?.data?.error || 'Error guardando campos');
        }
      } else {
        setError(e.response?.data?.error || 'Error guardando campos');
      }
    } finally {
      setGuardando(false);
    }
  };

  const setValor = (key, val) => {
    setValores((prev) => {
      const next = { ...prev, [key]: val };
      if (key === 'propietario_telefono' || key === 'telefono' || key === 'email') {
        relacionContacto.check(
          key === 'email' ? next.propietario_telefono || next.telefono : (key === 'propietario_telefono' || key === 'telefono' ? val : (next.propietario_telefono || next.telefono)),
          key === 'email' ? val : next.email,
        );
      }
      return next;
    });
  };

  /** Estilo de controles como en la app móvil (.f-inp / .f-sel-btn): beige vacío, navy con valor. */
  const movilInpClass = (val) => claseInpMovil(campoTieneValor(val));
  const movilLabel = {
    display: 'block',
    marginBottom: 6,
    marginTop: 0,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: LABEL_MOVIL,
    WebkitTextFillColor: LABEL_MOVIL,
  };
  const movilSelBtn = (activo) => estiloSelBtnMovil(activo);

  const abrirVinculoSi = (key, primera = true) => {
    if (toggleUsaModalDetalle(key)) {
      setModalVinculo({ tipo: 'detalle', key, primera });
      return true;
    }
    if (toggleUsaModalSelector(key)) {
      setModalVinculo({ tipo: 'selector', key, primera });
      return true;
    }
    if (toggleUsaModalFlags(key)) {
      setModalVinculo({ tipo: 'flags', key, primera });
      return true;
    }
    if (key === 'exclusiva') {
      // Campos exclusiva ya se muestran inline bajo el toggle
      return false;
    }
    return false;
  };

  const quitarVinculo = (toggleKey) => {
    const patch = { [toggleKey]: false };
    if (TOGGLE_SELECTOR_CFG[toggleKey]) patch[TOGGLE_SELECTOR_CFG[toggleKey].detailKey] = '';
    if (toggleKey === 'eq_garaje') {
      Object.assign(patch, buildGarajePlazasPatch(0), {
        num_garaje: '',
        garaje_tipos: [],
        garajes_plazas_detalle: [],
      });
    }
    if (toggleKey === 'eq_trastero') patch.num_trastero = '';
    if (toggleKey === 'vpo') {
      patch.vpo_fecha_fin = '';
      patch.vpo_ano_fin = '';
    }
    if (toggleKey === 'cocina_amueblada') patch.cocina_amueblada_obs = '';
    if (toggleKey === 'armarios') patch.armarios_obs = '';
    if (TOGGLE_FLAGS_CFG[toggleKey]) {
      (TOGGLE_FLAGS_CFG[toggleKey].fields || []).forEach((f) => {
        patch[f.key] = false;
        if (f.obs) patch[`${f.key}_obs`] = '';
      });
    }
    setValores((prev) => ({ ...prev, ...patch }));
    setModalVinculo(null);
  };

  const aplicarVinculo = (patch, opts = {}) => {
    setValores((prev) => ({ ...prev, ...patch }));
    setModalVinculo(null);
    if (opts.encadenarGarajeTipos) {
      const n = Math.max(1, parseInt(String(patch.num_plazas_garaje ?? valores.num_plazas_garaje ?? 1), 10) || 1);
      setTimeout(() => setModalVinculo({ tipo: 'garaje_tipos', plazas: n, primera: true }), 80);
    }
  };

  const renderAuxBotonesOSelect = (key, rows, selected, onPick, placeholder = 'Seleccionar…') => {
    const list = Array.isArray(rows) ? rows : [];
    // Aire / calefacción / piscina: siempre desplegable (como en la ficha CRM y petición)
    const fuerzaSelect = [
      'eq_aire', 'eq_aire_id', 'eq_calefaccion', 'eq_calefaccion_id',
      'eq_piscina_priv', 'eq_piscina_priv_id', 'eq_piscina_comun', 'eq_piscina_comun_id',
      'tipo_via_id', 'tipo_via', 'poblacion_id', 'provincia_id', 'agente_id',
    ].includes(key);
    if (!fuerzaSelect && list.length > 0 && list.length <= 10) {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {list.map((row) => {
            const id = String(row.id);
            const activo = String(selected || '') === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onPick(activo ? '' : id)}
                style={{ ...movilSelBtn(activo), flex: '1 1 calc(33.33% - 6px)', minWidth: 88, fontSize: 14 }}
              >
                {row.nombre}
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <InpCampoMovil as="select" value={selected} onChange={(e) => onPick(e.target.value)}>
        <option value="">{placeholder}</option>
        {list.map((row) => (
          <option key={row.id} value={row.id}>{row.nombre}</option>
        ))}
      </InpCampoMovil>
    );
  };

  const renderInput = (campo) => {
    const tipo = getTipo(campo.campo_key, campo.tipo_campo);
    const key = campo.campo_key;
    const val = valorVisibleCampo(key, valores, capFresh || captacion, propiedadVinculada);

    // Desplegable Provincia
    if (key === 'provincia_id' || key === 'provincia') {
      return (
        <InpCampoMovil
          as="select"
          value={val}
          onChange={(e) => {
            const v = e.target.value;
            setValor(key, v);
            if (key === 'provincia_id') setValor('poblacion_id', '');
            if (key === 'provincia') setValor('poblacion', '');
          }}
        >
          <option value="">Seleccionar provincia</option>
          {PROVINCIAS_ES.map((p) => (
            <option key={p.id} value={key === 'provincia_id' ? p.id : p.nombre}>{p.nombre}</option>
          ))}
        </InpCampoMovil>
      );
    }

    // Desplegable Agente responsable
    if (key === 'agente_id' || key === 'agente_responsable') {
      return (
        <InpCampoMovil as="select" value={val} onChange={(e) => setValor(key, e.target.value)}>
          <option value="">Seleccionar agente</option>
          {agentes.map((a) => (
            <option key={a.id} value={key === 'agente_id' ? a.id : a.nombre}>{a.nombre}</option>
          ))}
        </InpCampoMovil>
      );
    }

    // Desplegable Población (depende de provincia)
    if (key === 'poblacion_id' || key === 'poblacion') {
      return (
        <InpCampoMovil as="select" value={val} onChange={(e) => setValor(key, e.target.value)}>
          <option value="">{provinciaIdActual ? 'Seleccionar población' : 'Primero selecciona provincia'}</option>
          {poblaciones.map((p) => (
            <option key={p.id} value={key === 'poblacion_id' ? p.id : p.nombre}>{p.nombre}</option>
          ))}
        </InpCampoMovil>
      );
    }

    // Tipo de vía: siempre select nativo (lista larga), como en móvil
    if (key === 'tipo_via_id' || key === 'tipo_via') {
      const rows = tiposVia;
      const selected = normalizeAuxSelectValue(val, rows);
      return (
        <InpCampoMovil
          as="select"
          value={selected}
          onChange={(e) => {
            const id = e.target.value;
            setValores((prev) => ({ ...prev, ...buildAuxExtrasPatch(key, id) }));
          }}
        >
          <option value="">Seleccionar tipo de vía</option>
          {rows.map((tv) => (
            <option key={tv.id} value={tv.id}>{tv.nombre}</option>
          ))}
        </InpCampoMovil>
      );
    }

    // Tipo de inmueble
    if (key === 'tipo_inmueble_id' || key === 'tipo_inmueble') {
      const rows = tiposInmueble;
      const selected = normalizeAuxSelectValue(val, rows);
      return renderAuxBotonesOSelect(key, rows, selected, (id) => {
        setValores((prev) => ({ ...prev, ...buildAuxExtrasPatch(key, id) }));
      }, 'Seleccionar tipo de inmueble');
    }

    // Operación — botones como móvil
    if (key === 'operacion') {
      return (
        <div style={{ display: 'flex', gap: 7 }}>
          {OPCIONES_OPERACION.map((op) => (
            <button
              key={op}
              type="button"
              onClick={() => setValor(key, val === op ? '' : op)}
              style={movilSelBtn(val === op)}
            >
              {op}
            </button>
          ))}
        </div>
      );
    }

    const auxSlug = getAuxSlugForCampoKey(key);
    if (isVistasMultiCampo(key)) {
      const rows = auxLists.vistas || [];
      const selectedIds = normalizeVistasIdsFromExtras({ ...extrasCap, ...valores, ub_vistas_ids: valores.ub_vistas_ids, ub_vistas_id: val });
      return (
        <MultiSelectAuxVistas
          rows={rows}
          selectedIds={selectedIds}
          onChange={(ids) => setValores((prev) => ({ ...prev, ...buildVistasExtrasPatch(ids) }))}
        />
      );
    }
    if (auxSlug) {
      const rowsRaw = auxLists[auxSlug] || [];
      const rows = (auxSlug === 'aire-acondicionado' || auxSlug === 'calefaccion')
        ? ordenarOpcionesTipoEquipoDesplegable(rowsRaw)
        : rowsRaw;
      // Ampliar: padre eq_aire/eq_calefaccion → valor del *_id (select_native), no el boolean
      const cfgToggle = TOGGLE_SELECTOR_CFG[key];
      const valSelect = cfgToggle
        ? (valores[cfgToggle.detailKey] || (typeof val === 'string' && val.includes('-') ? val : '') || '')
        : val;
      const selected = normalizeAuxSelectValue(valSelect, rows);
      return renderAuxBotonesOSelect(key, rows, selected, (id) => {
        const patch = buildAuxExtrasPatch(key, id);
        const padre = padreDeDetailKey(key) || (cfgToggle ? key : null);
        if (padre) {
          if (!id) {
            patch[padre] = false;
            if (cfgToggle) patch[cfgToggle.detailKey] = '';
          } else {
            const row = rows.find((r) => String(r.id) === String(id));
            const nom = String(row?.nombre || '')
              .toLowerCase()
              .normalize('NFD')
              .replace(/\p{M}/gu, '')
              .trim();
            const ausente = !nom
              || /^(no tiene|ninguno|ninguna|sin|n\/a|-)$/.test(nom)
              || /^no tiene\b/.test(nom)
              || /^sin\s+(aire|aa|a\.?\s*a\.?|calefaccion|calef\.?)/.test(nom);
            // «No tiene» / «Sin aire» = No (sin dejar *_id colgando → bloqueaba guardar)
            patch[padre] = !ausente;
            if (ausente) {
              patch[key] = '';
              if (cfgToggle) patch[cfgToggle.detailKey] = '';
              else patch[`${padre}_id`] = '';
            } else if (cfgToggle) {
              patch[cfgToggle.detailKey] = id;
            }
          }
        }
        setValores((prev) => ({ ...prev, ...patch }));
      });
    }

    // Propietario: nombre + teléfono juntos
    if (key === 'propietario') {
      const tel = valores.propietario_telefono ?? '';
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <InpCampoMovil
            type="text"
            placeholder="Nombre del propietario"
            value={val}
            onChange={(e) => setValor(key, e.target.value)}
          />
          <div>
            <div className="agente-f-label" style={movilLabel}>Teléfono del propietario</div>
            <InpCampoMovil
              type="tel"
              placeholder="Ej: 612 345 678"
              value={tel}
              onChange={(e) => setValor('propietario_telefono', e.target.value)}
            />
          </div>
        </div>
      );
    }

    // Teléfono del propietario como campo independiente
    if (key === 'propietario_telefono') {
      return (
        <InpCampoMovil
          type="tel"
          placeholder="Ej: 612 345 678"
          value={val}
          onChange={(e) => setValor(key, e.target.value)}
        />
      );
    }

    if (tipo === 'garaje_plazas') {
      const n = val === '' || val == null ? null : Number(val);
      return (
        <GarajePlazasButtons
          value={Number.isNaN(n) ? null : n}
          onChange={(next) => {
            const patch = buildGarajePlazasPatch(next);
            setValores((prev) => ({ ...prev, ...patch, [key]: next }));
            if (next != null && next > 0) {
              setModalVinculo({ tipo: 'garaje_tipos', plazas: next, primera: true });
            }
          }}
          chipStyle={(activo) => movilSelBtn(activo)}
        />
      );
    }

    if (tipo === 'boolean') {
      const selSi = val === true || val === 'true';
      const selNo = val === false || val === 'false';
      const abrirSi = () => {
        setValor(key, true);
        if (toggleUsaModalVinculo(key)) {
          abrirVinculoSi(key, !selSi);
        }
      };
      return (
        <div style={{ display: 'flex', gap: 7 }}>
          <button
            type="button"
            onClick={() => {
              if (selSi && toggleUsaModalVinculo(key)) {
                abrirVinculoSi(key, false);
                return;
              }
              abrirSi();
            }}
            style={movilSelBtn(selSi)}
          >
            Sí
          </button>
          <button
            type="button"
            onClick={() => {
              if (toggleUsaModalVinculo(key)) quitarVinculo(key);
              else setValor(key, selNo ? null : false);
            }}
            style={movilSelBtn(selNo)}
          >
            No
          </button>
        </div>
      );
    }
    if (tipo === 'textarea') {
      return (
        <InpCampoMovil
          as="textarea"
          value={val}
          onChange={(e) => setValor(campo.campo_key, e.target.value)}
          rows={3}
          style={{ resize: 'vertical', minHeight: 72, lineHeight: 1.5 }}
        />
      );
    }
    if (isMoneyField(key)) {
      return (
        <InpCampoMovil
          type="text"
          inputMode="numeric"
          placeholder="0 €"
          value={formatMoney(val)}
          onChange={(e) => setValor(key, e.target.value)}
        />
      );
    }
    const valorDate = key === 'disponible_desde' && !val ? hoyISO() : val;
    if ((key === 'nombre_via' || key === 'calle') && tipo === 'text') {
      const filled = campoTieneValor(val);
      return (
        <div
          className={`agente-f-wrap${filled ? ' has-value' : ''}`}
          style={{
            background: filled ? '#3b82f6' : '#e8e8e0',
            backgroundColor: filled ? '#3b82f6' : '#e8e8e0',
            border: `2px solid ${filled ? '#3b82f6' : BRD_MOVIL}`,
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          <AddressAutocompleteInput
            value={String(val ?? '')}
            onValueChange={(v) => setValor(campo.campo_key, v)}
            onPlaceAddressResolved={aplicarDireccionDesdePlaces}
            className={movilInpClass(val)}
            style={{
              colorScheme: 'light',
              width: '100%',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              background: 'transparent',
              backgroundColor: 'transparent',
              color: filled ? '#ffffff' : '#000000',
              WebkitTextFillColor: filled ? '#ffffff' : '#000000',
              padding: '13px 14px',
              fontSize: 15,
              fontFamily: 'inherit',
              borderRadius: 10,
              boxSizing: 'border-box',
            }}
          />
        </div>
      );
    }
    return (
      <InpCampoMovil
        type={tipo === 'number' ? 'number' : tipo === 'date' ? 'date' : 'text'}
        value={tipo === 'date' ? valorDate : val}
        onChange={(e) => setValor(campo.campo_key, e.target.value)}
      />
    );
  };

  if (!isOpen || !captacion) return null;

  const camposExclusiva = ['exclusiva_duracion', 'exclusiva_desde', 'exclusiva_hasta'];
  const camposExclusivaExtra = exclusivaActiva
    ? camposCompletos.filter((c) => camposExclusiva.includes(c.campo_key))
    : [];
  const SYN_EXCL_AGENTE = [
    { campo_key: 'exclusiva_duracion', campo_label: 'Duración exclusiva' },
    { campo_key: 'exclusiva_hasta', campo_label: 'Vencimiento exclusiva' },
  ];
  const synExclAgente = exclusivaActiva
    ? SYN_EXCL_AGENTE.filter((c) => {
        const raw = valores?.[c.campo_key] ?? extrasCap?.[c.campo_key]
          ?? (c.campo_key === 'exclusiva_duracion' ? (valores?.duracion_exclusiva ?? extrasCap?.duracion_exclusiva) : undefined);
        return raw === undefined || raw === null || String(raw).trim() === '';
      }).filter((c) => !camposFaltantes.some((f) => f.campo_key === c.campo_key)
        && !camposExclusivaExtra.some((f) => f.campo_key === c.campo_key))
    : [];
  const esDenia = esPoblacionDeniaAgente(capFresh || captacion, extrasCap, valores);
  const camposEdicion = [
    ...camposFaltantes,
    ...camposExclusivaExtra.filter((c) => !camposFaltantes.some((f) => f.campo_key === c.campo_key)),
    ...synExclAgente,
  ];
  let camposFaltantesVisibles = camposEdicion.filter((c) => {
    if (esClaveZonaAgente(c) && !esDenia) return false;
    if (esCampoUiEspecialAgente(c.campo_key)) return false;
    if (DETAIL_KEYS_OCULTAS.has(c.campo_key)) {
      const padre = padreDeDetailKey(c.campo_key);
      if (padre && !camposEdicion.some((x) => x.campo_key === padre)) {
        return false;
      }
      return false;
    }
    // Si hay toggle padre y también el *_id, ocultar el toggle (mostrar solo desplegable)
    if (TOGGLE_SELECTOR_CFG[c.campo_key]) {
      const detail = TOGGLE_SELECTOR_CFG[c.campo_key].detailKey;
      if (camposEdicion.some((x) => x.campo_key === detail)) return false;
    }
    if (CAMPOS_EXCLUSIVA_DEP.includes(c.campo_key) && !exclusivaActiva) return false;
    return true;
  });
  // Reinyectar toggles padre cuando solo faltaba un detalle no-desplegable (trastero, VPO…)
  {
    const keysVis = new Set(camposFaltantesVisibles.map((c) => c.campo_key));
    const inject = [];
    camposEdicion.forEach((c) => {
      if (!DETAIL_KEYS_OCULTAS.has(c.campo_key)) return;
      const padre = padreDeDetailKey(c.campo_key);
      if (!padre || keysVis.has(padre)) return;
      if (inject.some((x) => x.campo_key === padre)) return;
      inject.push({
        ...c,
        campo_key: padre,
        campo_label: LABEL_AGENTE_OVERRIDE[padre] || c.campo_label,
        tipo_campo: 'boolean',
      });
      keysVis.add(padre);
    });
    if (inject.length) camposFaltantesVisibles = [...inject, ...camposFaltantesVisibles];
  }
  // Si ocultamos todo lo especial y aún hay otros faltantes, no reintroducir firma/fotos
  // (antes el fallback volvía a pintar «Firma del propietario» duplicando el bloque Encargo).
  if (camposFaltantes.length > 0 && camposFaltantesVisibles.length === 0) {
    const otros = camposFaltantes.filter((c) => !esCampoUiEspecialAgente(c.campo_key));
    if (otros.length) camposFaltantesVisibles = otros;
  }
  const camposOrdenados = (() => {
    const dependientes = new Set(CAMPOS_EXCLUSIVA_DEP);
    const depsMap = new Map(camposFaltantesVisibles.filter((c) => dependientes.has(c.campo_key)).map((c) => [c.campo_key, c]));
    const base = camposFaltantesVisibles.filter((c) => !dependientes.has(c.campo_key));
    const out = [];
    base.forEach((c) => {
      out.push(c);
      if (c.campo_key === 'exclusiva') {
        CAMPOS_EXCLUSIVA_DEP.forEach((k) => { if (depsMap.has(k)) out.push(depsMap.get(k)); });
      }
    });
    return out;
  })();
  // Ampliar: si falta algún campo de dirección, mostrar también los ya completos
  // (calle/nº/población) para que se vean los datos traídos de precaptación.
  // No inyectar zona fuera de Dénia ni campos «completos» vacíos (p. ej. zona
  // dada por satisfecha sin valor): el agente solo debe ver lo que sí se pide.
  const capVista = capFresh || captacion;
  const camposOrdenadosEfectivos = camposOrdenados.filter(
    (c) => !campoAgenteYaCubierto(c, valores, capVista, propiedadVinculada),
  );
  const keysPendientesSet = new Set(camposOrdenadosEfectivos.map((c) => c.campo_key));
  // Ampliar: incluir especiales (Fotos, Encargo) que no van en camposOrdenadosEfectivos.
  const resumenPendientes = [...new Set(
    [
      ...camposOrdenadosEfectivos.map((c) => labelCampoAgente(c)),
      ...camposFaltantes
        .filter((c) => {
          if (!esCampoUiEspecialAgente(c.campo_key)) return false;
          if (CAMPOS_EXCLUSIVA_DEP.includes(c.campo_key) && !exclusivaActiva) return false;
          if (esClaveZonaAgente(c) && !esDenia) return false;
          if (campoAgenteYaCubierto(c, valores, capVista, propiedadVinculada)) return false;
          // Encargo ya cerrado en banner → no listarlo como pendiente.
          if (c.campo_key === 'firma_captacion' && (
            extrasCap.encargo_revision_manual_ok === true
            || extrasCap.encargo_documento_manual === true
            || camposCompletos.some((x) => x.campo_key === 'firma_captacion')
          )) return false;
          return true;
        })
        .map((c) => labelCampoAgente(c)),
    ],
  )]
    .filter(Boolean)
    .join(', ');
  const camposParaBloques = (() => {
    const tieneAddrPendiente = camposOrdenadosEfectivos.some((c) => esClaveDireccionAgente(c));
    if (!tieneAddrPendiente) return camposOrdenadosEfectivos;
    const keysPend = keysPendientesSet;
    const completosAddr = camposCompletos.filter((c) => {
      if (!esClaveDireccionAgente(c) || keysPend.has(c.campo_key)) return false;
      if (esClaveZonaAgente(c) && !esDenia) return false;
      const v = valorVisibleCampo(c.campo_key, valores, capVista, propiedadVinculada);
      if (v == null || v === '') return false;
      return true;
    });
    const ensureKeys = ['nombre_via', 'numero', 'tipo_via_id', 'tipo_via', 'poblacion_id', 'poblacion', 'codigo_postal'];
    ensureKeys.forEach((k) => {
      if (keysPend.has(k) || completosAddr.some((c) => c.campo_key === k)) return;
      if (aliasesCampoAgente(k).some((a) => keysPend.has(a) || completosAddr.some((c) => c.campo_key === a))) return;
      const v = valorVisibleCampo(k, valores, capVista, propiedadVinculada);
      if (v == null || v === '') return;
      if (k === 'poblacion' && (keysPend.has('poblacion_id') || completosAddr.some((c) => c.campo_key === 'poblacion_id'))) return;
      if (k === 'poblacion_id' && (keysPend.has('poblacion') || completosAddr.some((c) => c.campo_key === 'poblacion'))) return;
      completosAddr.push({
        campo_key: k,
        campo_label: LABEL_AGENTE_OVERRIDE[k] || k,
        tipo_campo: (k === 'poblacion_id' || k === 'tipo_via_id') ? 'select' : 'text',
      });
    });
    return dedupeCamposAliasAgente([...completosAddr, ...camposOrdenadosEfectivos]);
  })();
  const bloquesPendientes = agruparCamposPendientesAgente(camposParaBloques);
  const requiereEncargo = [...camposFaltantes, ...camposCompletos].some((c) => c.campo_key === 'firma_captacion');
  const encargoFirmasDetalle = Array.isArray(extrasCap.encargo_firmas_detalle)
    ? extrasCap.encargo_firmas_detalle
    : [];
  const nombreFirmanteNorm = (n) => String(n || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const firmadosViafirmaNombres = (encargoViafirma.firmados || [])
    .map((p) => nombreFirmanteNorm(p.nombre))
    .filter(Boolean);
  const firmanteYaEnViafirma = (nombre) => {
    const n = nombreFirmanteNorm(nombre);
    if (!n || !firmadosViafirmaNombres.length) return false;
    return firmadosViafirmaNombres.some((fn) => n === fn || n.includes(fn) || fn.includes(n));
  };
  const encargoFirmadosDetalle = encargoFirmasDetalle
    .filter((f) => {
      if (!f) return false;
      const e = String(f.estado || '').toLowerCase();
      return e === 'firmado' || e.startsWith('firmado_') || e === 'ok' || e === 'done' || firmanteYaEnViafirma(f.nombre);
    })
    .map((f) => ({
      nombre: String(f.nombre || 'Firmante').trim(),
      canal: String(f.canal || '').toLowerCase(),
    }));
  const encargoPendientesDetalle = encargoFirmasDetalle
    .filter((f) => {
      if (!f) return false;
      const e = String(f.estado || 'pendiente').toLowerCase();
      if (e === 'firmado' || e.startsWith('firmado_') || e === 'ok' || e === 'done') return false;
      // Ampliar: ViaFirma ya lo dio por firmado aunque el detalle local diga pendiente.
      if (firmanteYaEnViafirma(f.nombre)) return false;
      return true;
    })
    .map((f) => {
      const canal = String(f.canal || '').toLowerCase();
      const canalLabel = ({
        dispositivo: 'Este dispositivo',
        whatsapp: 'WhatsApp',
        whatsapp_sms: 'WhatsApp/SMS',
        email: 'Email',
        papel: 'En papel',
        ambos: 'Email/WhatsApp',
      })[canal] || canal || '—';
      return {
        nombre: String(f.nombre || 'Firmante').trim(),
        canal,
        canalLabel,
      };
    });
  const firmaCapRaw = String(extrasCap.firma_captacion || '').trim();
  // Ampliar: el PDF puede estar en captacion_documentos (nota_encargo) sin
  // rellenar documento_firmado_url → verde «Encargo» sin enlace Ver/Descargar
  // (Xeresa Ana: doc «Encarrec de Ana playa», url extras null).
  const urlEncargoDesdeDocs = (() => {
    const lista = Array.isArray(documentosCap) ? documentosCap : [];
    const doc = lista.find((d) => {
      const t = String(d?.tipo_codigo || '').toLowerCase();
      return t === 'nota_encargo' || t === 'contrato_exclusiva';
    });
    const u = String(doc?.url || '').trim();
    return u && !u.startsWith('data:') ? u : '';
  })();
  const urlEncargoVer = (() => {
    const u = String(extrasCap.documento_firmado_url || '').trim();
    if (u && !u.startsWith('data:')) return u;
    return urlEncargoDesdeDocs;
  })();
  // Ana Benavent 28-oct: PDF validado manual (verde) + ViaFirma dejado en
  // firma_captacion='pendiente' → cuadro ámbar «Encargo pendiente» falso.
  // Si el backend ya da firma_captacion por completo, o hay revisión/manual OK,
  // el banner debe ir en verde (misma verdad que campoRelleno).
  const encargoDocCerrado = !!(
    extrasCap.encargo_revision_manual_ok === true
    || extrasCap.encargo_documento_manual === true
    || !!urlEncargoVer
    || camposCompletos.some((c) => c.campo_key === 'firma_captacion')
  );
  const encargoCompleto = encargoDocCerrado || (
    !(
      extrasCap.encargo_papel_pendiente === true
      || extrasCap.encargo_papel_pendiente === 'true'
      || firmaCapRaw === 'pendiente'
      || encargoPendientesDetalle.length > 0
      || camposFaltantes.some((c) => c.campo_key === 'firma_captacion')
    ) && !!(
      (firmaCapRaw && firmaCapRaw !== 'pendiente' && firmaCapRaw !== 'false')
      || extrasCap.documento_firmado_url
      || extrasCap.encargo_documento_manual
    )
  );
  const completados = camposCompletos.length;
  // Misma lista para contador y texto «Faltan N: …». Antes fotos/encargo (UI
  // especial) sumaban en `falta` pero no salían en el resumen → «Faltan 1» sin nombre.
  const camposFaltaEfectivos = camposFaltantes.filter((c) => {
    if (CAMPOS_EXCLUSIVA_DEP.includes(c.campo_key) && !exclusivaActiva) return false;
    if (esClaveZonaAgente(c) && !esDenia) return false;
    if (campoAgenteYaCubierto(c, valores, capVista, propiedadVinculada)) return false;
    // Encargo cerrado en banner (PDF/manual OK) no cuenta aunque ViaFirma diga pendiente.
    if (c.campo_key === 'firma_captacion' && encargoDocCerrado) return false;
    return true;
  });
  const falta = camposFaltaEfectivos.length;
  const faltaNotaSimpleDoc = camposFaltantes.some((c) => esCampoNotaSimpleDoc(c.campo_key));
  const faltaInputs = camposOrdenadosEfectivos.length;
  const progreso = total > 0 ? Math.round((completados / total) * 100) : 0;

  const renderCampoPendienteMovil = (campo, { lastInGroup = false } = {}) => {
    const esPendiente = keysPendientesSet.has(campo.campo_key);
    return (
    <div
      key={campo.campo_key}
      style={{
        marginBottom: lastInGroup ? 0 : 12,
        paddingBottom: lastInGroup ? 0 : 10,
        borderBottom: lastInGroup ? 'none' : (isDark ? `1px solid ${c.border}` : '1px solid #e5e7eb'),
        paddingLeft: esPendiente ? 10 : 0,
        borderLeft: esPendiente ? '4px solid #f97316' : 'none',
      }}
    >
      <label className="agente-f-label" style={movilLabel}>
        {labelCampoAgente(campo)}
        {esPendiente ? ' · falta' : ''}
      </label>
      {renderInput(campo)}
    </div>
    );
  };

  // Texto secundario (antes gris casi ilegible sobre el fondo oscuro): en modo oscuro se acerca al blanco.
  const textoInfo = isDark ? '#f1f5f9' : c.textMuted;
  // Borde de los contenedores: más claro que el borde estándar del tema para que se distinga bien sobre el fondo oscuro.
  const bordeFuerte = isDark ? '#5b6478' : c.border;

  // Colores amber para la etapa agente
  const amber = {
    from: '#d97706', to: '#f59e0b',
    shadow: 'rgba(245,158,11,0.3)',
    text: '#fef3c7',
    bg8: isDark ? 'rgba(245,158,11,0.08)' : '#fffbeb',
    border: isDark ? 'rgba(245,158,11,0.25)' : '#fde68a',
    green8: isDark ? 'rgba(34,197,94,0.08)' : '#f0fdf4',
    greenBorder: isDark ? 'rgba(34,197,94,0.25)' : '#bbf7d0',
  };

  return (
    <div onClick={() => { if (!guardando) onClose?.(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div ref={dragRef} onClick={e => e.stopPropagation()} style={{
        width: 540, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        background: isDark ? c.bgCard : '#c8c8be', borderRadius: 16, overflow: 'hidden',
        border: `3px solid ${isDark ? bordeFuerte : '#a8a8a0'}`,
        boxShadow: isDark
          ? '0 0 60px 15px rgba(255,255,255,0.15), 0 0 120px 40px rgba(255,255,255,0.08)'
          : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
        position: pos ? 'fixed' : 'relative',
        ...(pos ? { left: pos.x, top: pos.y, margin: 0 } : {}),
      }}>

        {/* Confirmación avanzado */}
        {avanzado && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 14, background: c.bgCard,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 12, zIndex: 2,
          }}>
            <div style={{ fontSize: 52 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#22c55e' }}>¡Todos los campos completados!</div>
            <div style={{ fontSize: 14, color: textoInfo }}>La captación ha pasado automáticamente a</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#ec4899' }}>Para Revisar</div>
          </div>
        )}

        {/* Barra superior amber */}
        <div style={{ height: 3, flexShrink: 0, background: `linear-gradient(90deg, #fbbf24, ${amber.from}, ${amber.to}, ${amber.from}, #fbbf24)` }} />

        {/* Header */}
        <div onMouseDown={startDrag} style={{
          padding: '14px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexShrink: 0, cursor: 'grab', userSelect: 'none',
          background: isDark ? 'transparent' : '#e0e0d8',
          borderBottom: `3px solid ${isDark ? bordeFuerte : '#a8a8a0'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: `linear-gradient(135deg, ${amber.from}, ${amber.to})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 12px ${amber.shadow}`,
            }}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: c.textPrimary }}>
                {titulo || 'Completar campos del agente'}
              </h2>
              <p style={{ margin: 0, marginTop: 1, fontSize: 12, color: textoInfo }}>
                {(capFresh || captacion).nombre} {(capFresh || captacion).apellido || ''} · {tipoInmuebleLegible(capFresh || captacion, extrasCap) || (capFresh || captacion).calle || '—'}
                {onOpenFicha && verCompletarFicha && (
                  <>
                    {' · '}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => { onOpenFicha(capFresh || captacion); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpenFicha(capFresh || captacion);
                        }
                      }}
                      style={{ color: LABEL_MOVIL, cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                    >Ver ficha completa</span>
                  </>
                )}
                {onOpenModalOriginal && (
                  <>
                    {' · '}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => { onOpenModalOriginal(capFresh || captacion); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpenModalOriginal(capFresh || captacion);
                        }
                      }}
                      style={{ color: LABEL_MOVIL, cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                    >ver modal original</span>
                  </>
                )}
              </p>
            </div>
          </div>
          <button onClick={() => { if (!guardando) onClose?.(); }} style={{
            width: 30, height: 30, borderRadius: 8, border: `2px solid ${bordeFuerte}`,
            background: c.bgInput, cursor: 'pointer', fontSize: 15, color: c.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        <div style={{ height: 1, flexShrink: 0, background: `linear-gradient(90deg, transparent, ${c.border}, transparent)`, margin: '14px 22px 0' }} />

        {/* Cuerpo scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '14px 22px 0' }}>

          {cargando ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: textoInfo, fontSize: 13 }}>
              Cargando campos obligatorios...
            </div>
          ) : (
            <>
              {/* Encargo firmado: URL en extras o documento nota_encargo/exclusiva */}
              {urlEncargoVer && (
                <a
                  href={urlEncargoVer}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', borderRadius: 10, marginBottom: 14,
                    background: isDark ? 'rgba(34,197,94,0.10)' : '#f0fdf4',
                    border: `1px solid ${isDark ? 'rgba(34,197,94,0.35)' : '#bbf7d0'}`,
                    color: isDark ? '#4ade80' : '#16a34a',
                    textDecoration: 'none', fontSize: 13, fontWeight: 600,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(34,197,94,0.25)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/>
                  </svg>
                  ✅ Encargo de venta firmado — Ver / Descargar PDF
                </a>
              )}

              {requiereEncargo && (
                <div style={{
                  marginBottom: 14, padding: '12px 14px', borderRadius: 10,
                  background: encargoCompleto ? amber.green8 : amber.bg8,
                  border: `3px solid ${encargoCompleto ? amber.greenBorder : amber.border}`,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: encargoCompleto ? '#16a34a' : LABEL_MOVIL, marginBottom: 8 }}>
                    {encargoCompleto ? '✓ Encargo de venta' : '⚠️ Encargo de venta pendiente'}
                  </div>
                  {!encargoCompleto && (
                    <>
                      {encargoPendientesDetalle.length > 0 ? (
                        <div style={{ fontSize: 12, color: textoInfo, lineHeight: 1.45, marginBottom: 8 }}>
                          {(encargoFirmadosDetalle.length > 0 || encargoViafirma.firmados?.length > 0) && (
                            <>
                              <strong style={{ color: isDark ? '#4ade80' : '#16a34a' }}>Ya firmado:</strong>
                              <ul style={{ margin: '6px 0 8px', paddingLeft: 18 }}>
                                {(encargoFirmadosDetalle.length
                                  ? encargoFirmadosDetalle
                                  : encargoViafirma.firmados
                                ).map((p) => (
                                  <li key={`ok-det-${p.nombre}`}>{p.nombre}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          <strong style={{ color: c.textPrimary }}>Falta firmar:</strong>
                          <ul style={{ margin: '6px 0 8px', paddingLeft: 18 }}>
                            {encargoPendientesDetalle.map((p) => (
                              <li key={`det-${p.nombre}-${p.canal}`}>
                                {p.nombre} — <em>{p.canalLabel}</em>
                              </li>
                            ))}
                          </ul>
                          {encargoPendientesDetalle.some((p) => p.canal === 'papel') && (
                            <div style={{ marginBottom: 8 }}>
                              Cuando el/los de papel firmen, escanea o adjunta el documento aquí (o desde la app).
                            </div>
                          )}
                        </div>
                      ) : encargoViafirma.cargando ? (
                        <div style={{ fontSize: 12, color: textoInfo, marginBottom: 8 }}>Consultando estado ViaFirma…</div>
                      ) : encargoViafirma.setCode ? (
                        <div style={{ fontSize: 12, color: textoInfo, lineHeight: 1.45, marginBottom: 8 }}>
                          {encargoViafirma.firmados?.length > 0 && (
                            <>
                              <strong style={{ color: isDark ? '#4ade80' : '#16a34a' }}>Ya firmado:</strong>
                              <ul style={{ margin: '6px 0 8px', paddingLeft: 18 }}>
                                {encargoViafirma.firmados.map((p) => (
                                  <li key={`ok-${p.nombre}`}>{p.nombre}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {encargoViafirma.pendientes.length > 0 ? (
                            <>
                              <strong style={{ color: c.textPrimary }}>Faltan firmantes (ViaFirma):</strong>
                              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                                {encargoViafirma.pendientes.map((p) => (
                                  <li key={p.nombre}>{p.nombre}</li>
                                ))}
                              </ul>
                            </>
                          ) : (
                            <>Solicitud ViaFirma en curso ({encargoViafirma.status || 'pendiente'}).</>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: textoInfo, lineHeight: 1.45, marginBottom: 8 }}>
                          No hay encargo firmado ni solicitud ViaFirma completada. Puedes subir el documento escaneado.
                        </div>
                      )}
                      {extrasCap.encargo_revision_manual_ok === false && (
                        <div style={{
                          fontSize: 12, color: isDark ? '#fca5a5' : '#b91c1c', lineHeight: 1.45,
                          marginBottom: 8, padding: '8px 10px', borderRadius: 8,
                          background: isDark ? 'rgba(185,28,28,0.15)' : '#fef2f2',
                        }}>
                          {extrasCap.encargo_revision_manual?.motivo
                            ? 'El último archivo no se reconoció como Encargo / Exclusiva de Vitalcasa. Súbelo de nuevo o usa ViaFirma.'
                            : 'El último archivo no se reconoció como Encargo / Exclusiva de Vitalcasa.'}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => inputEncargoRef.current?.click()}
                        disabled={subiendoEncargo}
                        style={{
                          width: '100%', padding: '10px 12px', borderRadius: 8, border: `2px dashed ${amber.border}`,
                          background: c.bgInput, color: LABEL_MOVIL, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                        }}
                      >
                        {subiendoEncargo ? 'Subiendo…' : '📎 Adjuntar encargo escaneado (PDF/foto)'}
                      </button>
                      <input ref={inputEncargoRef} type="file" accept="application/pdf,image/*" onChange={handleSubirEncargoDocumento} style={{ display: 'none' }} />
                    </>
                  )}
                </div>
              )}

              {/* Barra de progreso */}
              {total > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12 }}>
                    <span style={{ color: textoInfo, fontWeight: 700 }}>
                      {completados} de {total} campos completados
                    </span>
                    <span style={{ color: falta === 0 ? '#22c55e' : LABEL_MOVIL, fontWeight: 800 }}>
                      {falta === 0 ? '¡Todo listo!' : (resumenPendientes ? `Faltan ${falta}: ${resumenPendientes}` : `Faltan ${falta}`)}
                    </span>
                  </div>
                  <div style={{
                    height: 10, borderRadius: 6, background: c.bgInput, overflow: 'hidden',
                    border: `2px solid ${bordeFuerte}`,
                    boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.45)',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: 6, transition: 'width 0.4s ease',
                      width: `${progreso}%`,
                      background: falta === 0
                        ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                        : `linear-gradient(90deg, ${amber.to}, ${amber.from})`,
                      boxShadow: falta === 0
                        ? '0 0 10px 2px rgba(34,197,94,0.65)'
                        : `0 0 10px 2px ${amber.shadow}`,
                    }} />
                  </div>
                </div>
              )}

              {/* Hueco parado: arriba (sin scroll) + CTA verde antes del texto */}
              {multimediaHuecoParado && (
                <div style={{
                  padding: '12px 13px', borderRadius: 8, fontSize: 12, lineHeight: 1.5,
                  background: amber.bg8,
                  border: `2px solid ${amber.border}`,
                  color: isDark ? '#fcd34d' : '#92400e', marginBottom: 10,
                }}>
                  {falta === 0 && total > 0 && !avanzado && verCompletarAvanzar
                    && (!requiereEncargo || encargoCompleto) && (
                    <button
                      type="button"
                      onClick={handleEnviarParaRevisarSinPiezasFaltantes}
                      disabled={aceptandoMultimediaIncompleta || guardando || archivando || fotosSubidas <= 0}
                      style={{
                        display: 'block', width: '100%', boxSizing: 'border-box',
                        marginBottom: 10, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 800,
                        border: 'none', fontFamily: 'inherit',
                        background: aceptandoMultimediaIncompleta ? c.bgInput : '#22c55e',
                        color: aceptandoMultimediaIncompleta ? c.textMuted : '#111111',
                        cursor: (aceptandoMultimediaIncompleta || fotosSubidas <= 0) ? 'not-allowed' : 'pointer',
                        opacity: (aceptandoMultimediaIncompleta || fotosSubidas <= 0) ? 0.6 : 1,
                        boxShadow: (aceptandoMultimediaIncompleta || fotosSubidas <= 0) ? 'none' : '0 4px 14px rgba(34,197,94,0.35)',
                      }}
                    >
                      {aceptandoMultimediaIncompleta
                        ? 'Enviando…'
                        : 'Enviar a Para Revisar sin las piezas faltantes'}
                    </button>
                  )}
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Han faltado {textoHuecoMultimedia} por subir
                    {fotosEsperadas > 0 || videosEsperados > 0
                      ? ` (fotos ${fotosSubidas}/${fotosEsperadas || '—'} · vídeos ${videosSubidos}/${videosEsperados || '—'})`
                      : ''}
                    .
                  </div>
                  <div>
                    Puedes volver a subirlas desde el móvil o con «+ Foto» / «+ Vídeo» aquí.
                    Si ya no las tienes, envía la captación a Para Revisar con lo que sí ha llegado
                    (no se quedará esperando para siempre).
                  </div>
                </div>
              )}

              {/* Fotos y vídeos del inmueble — icono total (como Para Revisar); lightbox solo con eliminar */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: LABEL_MOVIL }}>
                    Fotos y vídeos del inmueble ({fotosReportaje.length + videosReportaje.length})
                    {camposFaltantes.some((c) => c.campo_key === 'fotos') && fotosSubidas === 0
                      ? ' · falta'
                      : ''}
                  </span>
                  {(subiendoFotos || subiendoVideos) && <span style={{ fontSize: 11, color: LABEL_MOVIL }}>Subiendo...</span>}
                </div>
                {camposFaltantes.some((c) => c.campo_key === 'fotos') && fotosSubidas === 0 && (
                  <div style={{
                    fontSize: 12, marginBottom: 8, padding: '8px 10px', borderRadius: 8,
                    background: amber.bg8,
                    border: `1px solid ${amber.border}`,
                    color: isDark ? '#fcd34d' : '#92400e',
                  }}>
                    Faltan las fotos del inmueble. Súbelas aquí o desde la app móvil (el encargo ya está).
                  </div>
                )}
                {subiendoVideos && (
                  <div style={{
                    fontSize: 12, marginBottom: 8, padding: '8px 10px', borderRadius: 8,
                    background: isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb',
                    border: `1px solid ${isDark ? 'rgba(245,158,11,0.35)' : '#fde68a'}`,
                    color: isDark ? '#fcd34d' : '#92400e',
                  }}>
                    Hay vídeos subiéndose. Hasta que terminen no se podrán reproducir en el visor.
                  </div>
                )}
                {/* Solo si la subida está viva. Si el hueco está parado, el aviso ámbar
                    de abajo dice la verdad (Josefa: «enviando 0/1» con subida_dormida). */}
                {videosEsperados > videosSubidos && (subiendoVideos || subiendoAhoraMm) && (
                  <div style={{
                    fontSize: 12, marginBottom: 8, padding: '8px 10px', borderRadius: 8,
                    background: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff',
                    border: `1px solid ${isDark ? 'rgba(59,130,246,0.35)' : '#bfdbfe'}`,
                    color: isDark ? '#93c5fd' : '#1e40af',
                  }}>
                    La app móvil está enviando vídeos ({videosSubidos}/{videosEsperados}). Se mostrarán al completar la subida.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'stretch' }}>
                  <button
                    type="button"
                    title="Ver fotos"
                    onClick={() => {
                      if (!fotosReportaje.length) return;
                      setLightboxVistaFoto('pantalla');
                      setLightboxFotoIdx(0);
                    }}
                    style={{
                      flex: '1 1 140px', minHeight: 88, borderRadius: 12, cursor: fotosReportaje.length ? 'pointer' : 'default',
                      fontFamily: 'inherit',
                      border: `3px solid ${bordeFuerte}`,
                      background: isDark ? c.bgInput : '#fff',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                      color: c.text, padding: 10, opacity: fotosReportaje.length ? 1 : 0.55,
                    }}
                  >
                    <span style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(124,58,237,0.35)',
                    }}>🖼️</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#fff' : '#0f172a' }}>Todas las fotos</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: isDark ? '#fff' : '#0f172a', letterSpacing: '0.02em' }}>
                      {fotosReportaje.length} foto{fotosReportaje.length === 1 ? '' : 's'}
                    </span>
                  </button>
                  <button
                    type="button"
                    title="Ver vídeos"
                    onClick={() => {
                      if (!videosReportaje.length) return;
                      setLightboxVistaVideo('pantalla');
                      setLightboxVideoIdx(0);
                    }}
                    style={{
                      flex: '1 1 140px', minHeight: 88, borderRadius: 12, cursor: videosReportaje.length ? 'pointer' : 'default',
                      fontFamily: 'inherit',
                      border: `3px solid ${bordeFuerte}`,
                      background: isDark ? c.bgInput : '#fff',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                      color: c.text, padding: 10, opacity: videosReportaje.length ? 1 : 0.55,
                    }}
                  >
                    <span style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(220,38,38,0.35)',
                    }}>🎥</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#fff' : '#0f172a' }}>Todos los vídeos</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: isDark ? '#fff' : '#0f172a', letterSpacing: '0.02em' }}>
                      {videosReportaje.length} vídeo{videosReportaje.length === 1 ? '' : 's'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => inputFotoRef.current?.click()}
                    style={{
                      width: 88, minHeight: 88, borderRadius: 12, border: `3px dashed ${bordeFuerte}`,
                      background: c.bgInput, cursor: 'pointer',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 3,
                      color: textoInfo, fontSize: 9, fontWeight: 600, fontFamily: 'inherit',
                    }}
                  >
                    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    + Foto/Vídeo
                  </button>
                </div>
                <input
                  ref={inputFotoRef}
                  type="file"
                  accept="image/*,video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                  multiple
                  onChange={handleSubirFotosReportaje}
                  style={{ display: 'none' }}
                />
                <div style={{ fontSize: 10, color: textoInfo, marginTop: 6 }}>
                  Fotos y vídeos reales del inmueble (mismo botón para ambos). Pulsa un icono para verlos o eliminarlos. Los que queden serán el nuevo total.
                </div>
              </div>

              {/* Documentos — mismo layout que fotos */}
              <div style={{
                marginBottom: 14,
                ...(faltaNotaSimpleDoc ? {
                  padding: 10,
                  borderRadius: 12,
                  border: `3px solid ${isDark ? 'rgba(249,115,22,0.55)' : '#f97316'}`,
                  background: isDark ? 'rgba(245,158,11,0.08)' : '#fff7ed',
                } : {}),
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: LABEL_MOVIL }}>
                    Documentos ({documentosCap.length})
                    {faltaNotaSimpleDoc ? ' — pendiente' : ''}
                  </span>
                  {subiendoDocs && <span style={{ fontSize: 11, color: LABEL_MOVIL }}>Subiendo...</span>}
                </div>
                {faltaNotaSimpleDoc && (
                  <div style={{
                    fontSize: 12, fontWeight: 700, lineHeight: 1.4, marginBottom: 10,
                    color: isDark ? '#fdba74' : '#9a3412',
                  }}>
                    Falta insertar nota simple o escritura donde se vean los m² construidos declarados.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'stretch' }}>
                  <button
                    type="button"
                    title="Ver documentos"
                    onClick={() => {
                      if (!documentosCap.length) return;
                      setLightboxVistaDoc('parrilla');
                      setLightboxDocIdx(0);
                    }}
                    style={{
                      flex: '1 1 140px', minHeight: 88, borderRadius: 12, cursor: documentosCap.length ? 'pointer' : 'default',
                      fontFamily: 'inherit',
                      border: `3px solid ${bordeFuerte}`,
                      background: isDark ? c.bgInput : '#fff',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                      color: c.text, padding: 10, opacity: documentosCap.length ? 1 : 0.55,
                    }}
                  >
                    <span style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(37,99,235,0.35)',
                    }}>📄</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#fff' : '#0f172a' }}>Todos los documentos</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: isDark ? '#fff' : '#0f172a', letterSpacing: '0.02em' }}>
                      {documentosCap.length} documento{documentosCap.length === 1 ? '' : 's'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => inputDocRef.current?.click()}
                    disabled={subiendoDocs}
                    style={{
                      width: 88, minHeight: 88, borderRadius: 12, border: `3px dashed ${bordeFuerte}`,
                      background: c.bgInput, cursor: subiendoDocs ? 'not-allowed' : 'pointer',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 3,
                      color: textoInfo, fontSize: 9, fontWeight: 600, fontFamily: 'inherit',
                    }}
                  >
                    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="12" y1="18" x2="12" y2="12"/>
                      <line x1="9" y1="15" x2="15" y2="15"/>
                    </svg>
                    + Doc
                  </button>
                </div>
                <input
                  ref={inputDocRef}
                  type="file"
                  accept="application/pdf,image/*,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
                  multiple
                  onChange={handleSubirDocumentos}
                  style={{ display: 'none' }}
                />
                <div style={{ fontSize: 10, color: textoInfo, marginTop: 6 }}>
                  {faltaNotaSimpleDoc
                    ? 'Sube aquí la nota simple o una foto de la escritura (m² declarados). El resto de docs (IBI, basuras…) también van aquí. Pulsa el cuadro para verlos, cambiar el nombre o eliminar.'
                    : 'Pulsa el cuadro para ver todos los documentos, editar el nombre o eliminar.'}
                </div>
              </div>

              {propietariosState && (camposPropietarioObl.length > 0 || faltaTelefonoPrincipalEnState(propietariosState)) && (
                <BloquePropietariosAgente
                  state={propietariosState}
                  onChange={setPropietariosState}
                  tiposVia={tiposVia}
                  camposObligatorios={camposPropietarioObl}
                  direccionInmueble={{
                    tipo_via_id: valores.tipo_via_id || extrasCap.tipo_via_id || propiedadVinculada?.tipo_via_id || '',
                    tipo_via: valores.tipo_via || extrasCap.tipo_via || propiedadVinculada?.tipo_via || '',
                    domicilio_base: valores.nombre_via || valores.calle || extrasCap.nombre_via
                      || propiedadVinculada?.nombre_via
                      || (capFresh || captacion)?.calle
                      || [(capFresh || captacion)?.calle, (capFresh || captacion)?.numero].filter(Boolean).join(', ')
                      || '',
                    calle: valores.nombre_via || valores.calle || propiedadVinculada?.nombre_via
                      || (capFresh || captacion)?.calle || '',
                    numero: valores.numero || propiedadVinculada?.numero
                      || (capFresh || captacion)?.numero || extrasCap.numero || '',
                    bloque: valores.bloque || extrasCap.bloque || propiedadVinculada?.bloque || '',
                    escalera: valores.escalera || extrasCap.escalera || propiedadVinculada?.escalera || '',
                    piso: valores.piso || extrasCap.piso || propiedadVinculada?.piso || '',
                    puerta: valores.puerta || extrasCap.puerta || propiedadVinculada?.puerta || '',
                    cp: valores.codigo_postal || extrasCap.codigo_postal || extrasCap.cp || extrasCap.domicilio_cp || '',
                    codigo_postal: valores.codigo_postal || extrasCap.codigo_postal || extrasCap.cp || '',
                    poblacion: poblaciones.find((p) => String(p.id) === String(valores.poblacion_id))?.nombre
                      || valores.poblacion || extrasCap.poblacion || extrasCap.domicilio_poblacion
                      || (capFresh || captacion)?.poblacion || '',
                    provincia: PROVINCIAS_ES.find((p) => p.id === String(valores.provincia_id || '').padStart(2, '0'))?.nombre
                      || valores.provincia || extrasCap.provincia || extrasCap.domicilio_provincia || '',
                    pais: 'ES',
                  }}
                  contactoContext={{
                    idioma: valores.idioma || extrasCap.idioma || 'es',
                    pais: 'ES',
                  }}
                />
              )}

              {/* Sin campos configurados */}
              {total === 0 && (
                <div style={{
                  padding: '16px 14px', borderRadius: 10, textAlign: 'center',
                  background: amber.bg8, border: `3px solid ${amber.border}`,
                  fontSize: 13, color: textoInfo,
                }}>
                  No hay campos obligatorios configurados para el agente.
                  <br />
                  <span style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                    Configúralos en <strong>Campos obligatorios Propiedad</strong>
                  </span>
                </div>
              )}

              {/* Campos faltantes — estética móvil, agrupados */}
              {faltaInputs > 0 && (
                <div className="agente-movil-campos" style={{
                  marginBottom: 12,
                  background: isDark ? 'rgba(245,158,11,0.08)' : '#fff7ed',
                  border: `3px solid ${isDark ? 'rgba(249,115,22,0.45)' : '#f97316'}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                }}>
                  <div style={{
                    fontSize: 12, fontWeight: 800, color: LABEL_MOVIL,
                    marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    ⚠️ Campos pendientes ({faltaInputs})
                    {resumenPendientes ? `: ${resumenPendientes}` : ''}
                  </div>
                  <div style={{ fontSize: 11, color: isDark ? '#86efac' : '#166534', marginBottom: 12, lineHeight: 1.4 }}>
                    {resumenPendientes
                      ? `Hay que rellenar: ${resumenPendientes}. El resto de la dirección es contexto.`
                      : 'Rellénalos aquí. La dirección acepta sugerencias de Google.'}
                  </div>
                  <div>
                    {bloquesPendientes.map((block, bi) => {
                      if (block.type === 'group') {
                        return (
                          <div
                            key={`grp-${block.grupo}-${bi}`}
                            style={{
                              marginBottom: bi < bloquesPendientes.length - 1 ? 14 : 0,
                              padding: '12px 12px 10px',
                              borderRadius: 12,
                              border: `3px solid ${isDark ? bordeFuerte : BRD_MOVIL}`,
                              background: isDark ? c.bgInput : '#fff',
                            }}
                          >
                            <div className="agente-f-label" style={{
                              fontSize: 12, fontWeight: 800,
                              color: LABEL_MOVIL,
                              textTransform: 'uppercase', letterSpacing: '0.06em',
                              margin: '0 0 10px',
                            }}>
                              {block.titulo}
                            </div>
                            {block.campos.map((campo, ci) =>
                              renderCampoPendienteMovil(campo, { lastInGroup: ci === block.campos.length - 1 }),
                            )}
                          </div>
                        );
                      }
                      return (
                        <div
                          key={block.campo.campo_key}
                          style={{
                            marginBottom: bi < bloquesPendientes.length - 1 ? 12 : 0,
                            paddingBottom: bi < bloquesPendientes.length - 1 ? 10 : 0,
                            borderBottom: bi < bloquesPendientes.length - 1
                              ? (isDark ? `1px solid ${c.border}` : '1px solid #e5e7eb')
                              : 'none',
                          }}
                        >
                          <label className="agente-f-label" style={movilLabel}>{labelCampoAgente(block.campo)}</label>
                          {renderInput(block.campo)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Aviso subida fotos móvil en curso */}
              {subidaFotosPendiente && !multimediaHuecoParado && (
                <div style={{
                  padding: '10px 13px', borderRadius: 8, fontSize: 12, lineHeight: 1.45,
                  background: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff',
                  border: `2px solid ${isDark ? 'rgba(59,130,246,0.35)' : '#bfdbfe'}`,
                  color: isDark ? '#93c5fd' : '#1d4ed8', marginBottom: 10,
                }}>
                  📷 Fotos/vídeos subiendo desde el móvil
                  {fotosEsperadas > 0 ? ` (fotos ${fotosSubidas} de ${fotosEsperadas}` : ''}
                  {videosEsperados > 0 ? `${fotosEsperadas > 0 ? ', ' : ' ('}vídeos ${videosSubidos} de ${videosEsperados}` : ''}
                  {fotosEsperadas > 0 || videosEsperados > 0 ? ')' : ''}.
                  Puedes rellenar y guardar los campos. El botón «Avanzar a Para Revisar» se activa cuando estén todas las fotos y vídeos.
                  También puedes añadir fotos aquí con «+ Foto».
                </div>
              )}

              {/* Guardado OK */}
              {guardadoOk && (
                <div style={{
                  padding: '10px 13px', borderRadius: 8, fontSize: 13,
                  background: isDark ? '#0d2818' : '#f0fdf4',
                  border: `2px solid ${isDark ? '#166534' : '#bbf7d0'}`,
                  color: '#22c55e', marginBottom: 10, fontWeight: 600,
                }}>✓ Campos guardados correctamente</div>
              )}

              {infoMultimedia && (
                <div style={{
                  padding: '10px 13px', borderRadius: 8, fontSize: 13, lineHeight: 1.45,
                  background: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff',
                  border: `2px solid ${isDark ? 'rgba(59,130,246,0.35)' : '#bfdbfe'}`,
                  color: isDark ? '#93c5fd' : '#1d4ed8', marginBottom: 10, fontWeight: 600,
                }}>✓ {infoMultimedia}</div>
              )}

              {/* Error */}
              {error && (
                <div style={{
                  padding: '10px 13px', borderRadius: 8, fontSize: 13,
                  background: isDark ? '#2a1215' : '#fef2f2',
                  border: `2px solid ${isDark ? '#5c1d1d' : '#fecaca'}`,
                  color: '#ef4444', marginBottom: 10,
                }}>{error}</div>
              )}
            </>
          )}

          <div style={{ height: 10 }} />
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, borderTop: `3px solid ${isDark ? bordeFuerte : '#a8a8a0'}` }}>
          {accionArchivo && (
            <div style={{
              padding: '14px 20px', borderBottom: `3px solid ${bordeFuerte}`,
              background: accionArchivo === 'frio' ? (isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff') : (isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2'),
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: accionArchivo === 'frio' ? '#3b82f6' : '#ef4444', marginBottom: 8 }}>
                {accionArchivo === 'frio' ? '❄️ Marcar como frío' : '🗑️ Eliminar captación'} — Motivo (opcional)
              </div>
              <textarea
                value={motivoArchivo}
                onChange={(e) => setMotivoArchivo(e.target.value)}
                placeholder="Ej: Propietario desiste, precio irreal, no contesta..."
                rows={2}
                style={{
                  width: '100%', padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
                  border: `1px solid ${c.border}`, borderRadius: 8,
                  background: c.bgInput, color: c.textPrimary, outline: 'none',
                  fontFamily: 'inherit', resize: 'none', marginBottom: 10, lineHeight: 1.5,
                }}
                onFocus={(e) => { e.target.style.borderColor = accionArchivo === 'frio' ? '#3b82f6' : '#ef4444'; }}
                onBlur={(e) => { e.target.style.borderColor = c.border; }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setAccionArchivo(null); setMotivoArchivo(''); }} style={{
                  flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  border: `1px solid ${c.border}`, background: 'transparent', color: textoInfo,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>Cancelar</button>
                <button onClick={handleArchivar} disabled={archivando} style={{
                  flex: 2, padding: '9px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                  border: 'none', cursor: archivando ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  background: accionArchivo === 'frio' ? '#3b82f6' : '#ef4444', color: '#fff',
                  opacity: archivando ? 0.6 : 1,
                }}>
                  {archivando ? 'Procesando...' : accionArchivo === 'frio' ? '❄️ Confirmar frío' : '🗑️ Confirmar eliminación'}
                </button>
              </div>
            </div>
          )}

          <div style={{
            padding: '13px 20px 18px', display: 'flex', gap: 8, alignItems: 'center',
            background: isDark ? 'transparent' : '#e0e0d8',
          }}>
            {!accionArchivo && (
              <>
                {verCompletarFrio && (
                <button onClick={() => { setAccionArchivo('frio'); setMotivoArchivo(''); }} disabled={guardando || archivando} style={{
                  padding: '9px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  border: `1px solid #3b82f640`, background: isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff',
                  color: '#3b82f6', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                  opacity: guardando || archivando ? 0.5 : 1,
                }}>❄️ Frío</button>
                )}
                {verCompletarEliminar && (
                <button onClick={() => { setAccionArchivo('eliminado'); setMotivoArchivo(''); }} disabled={guardando || archivando} style={{
                  padding: '9px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  border: `1px solid #ef444440`, background: isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2',
                  color: '#ef4444', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                  opacity: guardando || archivando ? 0.5 : 1,
                }}>🗑️ Eliminar</button>
                )}
              </>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={() => { if (!guardando && !archivando) onClose?.(); }} style={{
              padding: '10px 22px', borderRadius: 9, border: `1.5px solid ${isDark ? c.border : '#a8a8a0'}`,
              background: isDark ? 'transparent' : '#ffffff', color: c.textPrimary,
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
            }}>{guardadoOk ? 'Ir al pipeline' : 'Cancelar'}</button>
            {/* Footer: si falta multimedia, se queda en «Esperando…» (deshabilitado).
                La vía «sin piezas faltantes» solo está en el aviso de arriba (doble opción). */}
            {!accionArchivo && falta === 0 && total > 0 && !avanzado && verCompletarAvanzar
              && (!requiereEncargo || encargoCompleto) && (
              <button
                onClick={handleGuardar}
                disabled={guardando || archivando || avanceBloqueadoMultimedia}
                title={avanceBloqueadoMultimedia
                  ? (multimediaHuecoParado
                    ? `Han faltado ${textoHuecoMultimedia}. Re-sube o usa el botón verde de arriba para enviar sin esas piezas.`
                    : (faltaVideosMm && !faltaFotosMm
                      ? 'Espera a que terminen de subirse los vídeos del móvil'
                      : 'Espera a que terminen de subirse las fotos y vídeos del móvil'))
                  : undefined}
                style={{
                padding: '10px 26px', borderRadius: 9, border: 'none',
                background: (guardando || avanceBloqueadoMultimedia) ? c.bgInput : 'linear-gradient(135deg, #16a34a, #22c55e)',
                color: (guardando || avanceBloqueadoMultimedia) ? c.textMuted : '#fff',
                cursor: (guardando || avanceBloqueadoMultimedia) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                boxShadow: (guardando || avanceBloqueadoMultimedia) ? 'none' : '0 4px 14px rgba(34,197,94,0.35)',
                opacity: (guardando || avanceBloqueadoMultimedia) ? 0.6 : 1,
              }}>
                {guardando
                  ? 'Procesando...'
                  : avanceBloqueadoMultimedia
                    ? textoEsperandoAvance
                    : '🚀 Avanzar a Para Revisar'}
              </button>
            )}
            {/* Encargo enviado pero sin firmar: no ofrecer avance verde (falta puede ser 0 por valores fantasma). */}
            {!accionArchivo && falta === 0 && total > 0 && !avanzado && requiereEncargo && !encargoCompleto && verCompletarGuardar && (
              <button onClick={handleGuardar} disabled={guardando || cargando || archivando || !modCompletarGuardar} title={!modCompletarGuardar ? tituloSinPermisoAgente : 'Falta firma del encargo de venta'} style={{
                padding: '10px 26px', borderRadius: 9, border: 'none',
                background: guardando ? c.bgInput : `linear-gradient(135deg, ${amber.from}, ${amber.to})`,
                color: guardando ? c.textMuted : amber.text,
                cursor: (guardando || !modCompletarGuardar) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                boxShadow: guardando ? 'none' : `0 4px 14px ${amber.shadow}`,
                opacity: (guardando || !modCompletarGuardar) ? 0.6 : 1,
              }}>
                {guardando ? 'Guardando...' : '💾 Guardar campos'}
              </button>
            )}
            {!accionArchivo && falta > 0 && verCompletarGuardar && (
              <button onClick={handleGuardar} disabled={guardando || cargando || archivando || !modCompletarGuardar} title={!modCompletarGuardar ? tituloSinPermisoAgente : undefined} style={{
                padding: '10px 26px', borderRadius: 9, border: 'none',
                background: guardando ? c.bgInput : `linear-gradient(135deg, ${amber.from}, ${amber.to})`,
                color: guardando ? c.textMuted : amber.text,
                cursor: (guardando || !modCompletarGuardar) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                boxShadow: guardando ? 'none' : `0 4px 14px ${amber.shadow}`,
                opacity: (guardando || !modCompletarGuardar) ? 0.6 : 1,
              }}>
                {guardando ? 'Guardando...' : '💾 Guardar campos'}
              </button>
            )}
          </div>
        </div>
      </div>

      <ModalVinculosAgente
        modal={modalVinculo}
        valores={valores}
        auxLists={auxLists}
        onClose={() => setModalVinculo(null)}
        onApply={aplicarVinculo}
        onQuitar={quitarVinculo}
      />

      {lightboxFotoIdx !== null && fotosReportaje[lightboxFotoIdx] && (
        <div
          role="presentation"
          onClick={() => { setLightboxFotoIdx(null); setLightboxVistaFoto('pantalla'); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: 16, right: 16, display: 'flex', gap: 8, zIndex: 2,
            }}
          >
            <button
              type="button"
              title={lightboxVistaFoto === 'pantalla' ? 'Ver parrilla (3 por fila)' : 'Ver pantalla completa'}
              onClick={() => setLightboxVistaFoto((v) => (v === 'pantalla' ? 'parrilla' : 'pantalla'))}
              style={{
                padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.92)', color: '#111', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {lightboxVistaFoto === 'pantalla' ? <>▦ Parrilla</> : <>⛶ Pantalla</>}
            </button>
            <button
              type="button"
              onClick={() => { setLightboxFotoIdx(null); setLightboxVistaFoto('pantalla'); }}
              style={{
                padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
              }}
            >Cerrar</button>
          </div>
          <span style={{
            position: 'absolute', top: 20, left: 16, color: '#fff', fontSize: 12, fontWeight: 700,
            textShadow: '0 1px 4px #000',
          }}>
            Todas las fotos
            {lightboxVistaFoto === 'pantalla' ? ` · ${lightboxFotoIdx + 1} / ${fotosReportaje.length}` : ` · ${fotosReportaje.length}`}
          </span>

          {lightboxVistaFoto === 'parrilla' ? (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(1440px, 96vw)',
                maxHeight: '82vh',
                overflowY: 'auto',
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '8px 4px',
                alignContent: 'start',
              }}
            >
              {fotosReportaje.map((foto, idx) => {
                const activa = idx === lightboxFotoIdx;
                return (
                  <button
                    key={`lb-foto-grid-${foto.id || idx}`}
                    type="button"
                    onClick={() => {
                      setLightboxFotoIdx(idx);
                      setLightboxVistaFoto('pantalla');
                    }}
                    style={{
                      position: 'relative',
                      border: activa ? '3px solid #c084fc' : '1px solid #64748b',
                      borderRadius: 10, overflow: 'hidden', background: '#0f172a',
                      aspectRatio: '4 / 3',
                      cursor: 'pointer', padding: 0, fontFamily: 'inherit',
                    }}
                  >
                    <img
                      src={urlFotoGrid(foto) || urlFotoCompleta(foto) || foto.url}
                      alt=""
                      loading="lazy"
                      draggable={false}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                    />
                    <span style={{
                      position: 'absolute', bottom: 6, left: 6, padding: '2px 7px', borderRadius: 6,
                      fontSize: 11, fontWeight: 800, background: 'rgba(0,0,0,0.65)', color: '#fff',
                      pointerEvents: 'none',
                    }}>
                      {idx === 0 ? 'Portada' : `#${idx + 1}`}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              {fotosReportaje.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxFotoIdx((i) => (i > 0 ? i - 1 : fotosReportaje.length - 1));
                    }}
                    style={{
                      position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                      width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111',
                      background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                    }}
                  >‹</button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxFotoIdx((i) => (i < fotosReportaje.length - 1 ? i + 1 : 0));
                    }}
                    style={{
                      position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                      width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111',
                      background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                    }}
                  >›</button>
                </>
              )}
              <img
                src={urlFotoCompleta(fotosReportaje[lightboxFotoIdx]) || urlFotoGrid(fotosReportaje[lightboxFotoIdx]) || fotosReportaje[lightboxFotoIdx].url}
                alt=""
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: '96vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 8 }}
              />
              {fotosReportaje[lightboxFotoIdx]?.id
                && !String(fotosReportaje[lightboxFotoIdx].id).startsWith('temp-')
                && !String(fotosReportaje[lightboxFotoIdx].id).startsWith('ident-')
                && !fotosReportaje[lightboxFotoIdx]?.soloIdentificacion && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute', bottom: 56, left: '50%', transform: 'translateX(-50%)',
                    display: 'flex', gap: 10, justifyContent: 'center',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => eliminarFotoReportaje(fotosReportaje[lightboxFotoIdx].id)}
                    style={{
                      padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                      boxShadow: '0 4px 14px rgba(239,68,68,0.45)',
                    }}
                  >
                    Eliminar foto
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {lightboxVideoIdx !== null && videosReportaje[lightboxVideoIdx] && (
        <div
          role="presentation"
          onClick={() => { setLightboxVideoIdx(null); setLightboxVistaVideo('pantalla'); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: 16, right: 16, display: 'flex', gap: 8, zIndex: 2,
            }}
          >
            <button
              type="button"
              title={lightboxVistaVideo === 'pantalla' ? 'Ver parrilla (3 por fila)' : 'Ver pantalla completa'}
              onClick={() => setLightboxVistaVideo((v) => (v === 'pantalla' ? 'parrilla' : 'pantalla'))}
              style={{
                padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.92)', color: '#111', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {lightboxVistaVideo === 'pantalla' ? <>▦ Parrilla</> : <>⛶ Pantalla</>}
            </button>
            <button
              type="button"
              onClick={() => { setLightboxVideoIdx(null); setLightboxVistaVideo('pantalla'); }}
              style={{
                padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
              }}
            >Cerrar</button>
          </div>
          <span style={{
            position: 'absolute', top: 20, left: 16, color: '#fff', fontSize: 12, fontWeight: 700,
            textShadow: '0 1px 4px #000',
          }}>
            Todos los vídeos
            {lightboxVistaVideo === 'pantalla' ? ` · ${lightboxVideoIdx + 1} / ${videosReportaje.length}` : ` · ${videosReportaje.length}`}
          </span>

          {lightboxVistaVideo === 'parrilla' ? (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(1440px, 96vw)',
                maxHeight: '82vh',
                overflowY: 'auto',
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '8px 4px',
                alignContent: 'start',
              }}
            >
              {videosReportaje.map((vid, idx) => {
                const activa = idx === lightboxVideoIdx;
                const videoSrc = absolutizarMediaUrlAgente(vid?.url);
                const posterSrc = absolutizarMediaUrlAgente(vid?.url_thumb);
                const subiendoEste = !!(vid?.esLocal || subiendoVideos || String(vid?.id || '').startsWith('temp-'));
                return (
                  <button
                    key={`lb-vid-grid-${vid.id || idx}`}
                    type="button"
                    onClick={() => {
                      setLightboxVideoIdx(idx);
                      setLightboxVistaVideo('pantalla');
                    }}
                    style={{
                      position: 'relative',
                      border: activa ? '3px solid #c084fc' : '1px solid #64748b',
                      borderRadius: 10, overflow: 'hidden', background: '#0f172a',
                      aspectRatio: '4 / 3',
                      cursor: 'pointer', padding: 0, fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {posterSrc && !subiendoEste ? (
                      <img
                        src={posterSrc}
                        alt=""
                        draggable={false}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                      />
                    ) : videoSrc && !subiendoEste ? (
                      <video
                        src={videoSrc}
                        muted
                        preload="metadata"
                        playsInline
                        poster={posterSrc || undefined}
                        draggable={false}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                      />
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 28 }}>{subiendoEste ? '⏳' : '🎬'}</span>
                    )}
                    <span style={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      pointerEvents: 'none',
                    }}>
                      <span style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.55)', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, fontWeight: 800,
                      }}>▶</span>
                    </span>
                    <span style={{
                      position: 'absolute', bottom: 6, left: 6, padding: '2px 7px', borderRadius: 6,
                      fontSize: 11, fontWeight: 800, background: 'rgba(0,0,0,0.65)', color: '#fff',
                      pointerEvents: 'none',
                    }}>
                      #{idx + 1}{subiendoEste ? ' · subiendo' : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              {videosReportaje.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxVideoIdx((i) => (i > 0 ? i - 1 : videosReportaje.length - 1));
                    }}
                    style={{
                      position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                      width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111',
                      background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                    }}
                  >‹</button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxVideoIdx((i) => (i < videosReportaje.length - 1 ? i + 1 : 0));
                    }}
                    style={{
                      position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                      width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111',
                      background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                    }}
                  >›</button>
                </>
              )}
              {(() => {
                const vid = videosReportaje[lightboxVideoIdx];
                const videoSrc = absolutizarMediaUrlAgente(vid?.url);
                const subiendoEste = !!(vid?.esLocal || subiendoVideos || String(vid?.id || '').startsWith('temp-'));
                if (subiendoEste || !videoSrc) {
                  return (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        maxWidth: 420, padding: '28px 24px', borderRadius: 14, textAlign: 'center',
                        background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
                      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Vídeo subiéndose…</div>
                      <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.45 }}>
                        El archivo todavía se está enviando al servidor. Cuando termine la subida podrás reproducirlo aquí.
                      </div>
                    </div>
                  );
                }
                return (
                  <video
                    key={vid.id}
                    src={videoSrc}
                    controls
                    autoPlay
                    playsInline
                    onClick={(e) => e.stopPropagation()}
                    onError={(e) => {
                      try {
                        const wrap = e.currentTarget.parentElement;
                        e.currentTarget.remove();
                        if (!wrap || wrap.querySelector('[data-video-err]')) return;
                        const msg = document.createElement('div');
                        msg.setAttribute('data-video-err', '1');
                        msg.style.cssText = 'max-width:420px;padding:28px 24px;border-radius:14px;text-align:center;background:rgba(255,255,255,0.1);color:#fff;border:1px solid rgba(255,255,255,0.2)';
                        msg.innerHTML = '<div style="font-size:16px;font-weight:700;margin-bottom:8px">No se puede reproducir todavía</div><div style="font-size:13px;opacity:.85;line-height:1.45">Es posible que el vídeo siga subiéndose. Cierra el visor y vuelve a abrirlo en unos segundos.</div>';
                        wrap.appendChild(msg);
                      } catch (_) {}
                    }}
                    style={{ maxWidth: '96vw', maxHeight: '82vh', borderRadius: 8, background: '#000' }}
                  />
                );
              })()}
              {videosReportaje[lightboxVideoIdx]?.id && !String(videosReportaje[lightboxVideoIdx].id).startsWith('temp-') && !videosReportaje[lightboxVideoIdx]?.esLocal && !subiendoVideos && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute', bottom: 56, left: '50%', transform: 'translateX(-50%)',
                    display: 'flex', gap: 10, justifyContent: 'center',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => eliminarVideoReportaje(videosReportaje[lightboxVideoIdx].id)}
                    style={{
                      padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                      boxShadow: '0 4px 14px rgba(239,68,68,0.45)',
                    }}
                  >
                    Eliminar vídeo
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {lightboxDocIdx !== null && documentosCap[lightboxDocIdx] && (() => {
        const doc = documentosCap[lightboxDocIdx];
        const src = resolveCaptacionMediaUrl(doc.url);
        const kind = docVistaPreviaKindAgente(doc, src);
        const titulo = doc.nombre_documento || doc.nombre_original || 'Documento';
        const tipo = etiquetaTipoDocumento(doc.tipo_codigo) || doc.tipo_codigo || '';
        const inputNombre = (d, { color = '#fff' } = {}) => (
          editandoNombreDocId === d.id ? (
            <input
              autoFocus
              value={nombreDocDraft}
              onChange={(e) => setNombreDocDraft(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={() => guardarNombreDocumento(d)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  guardarNombreDocumento(d);
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setEditandoNombreDocId(null);
                }
              }}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
                border: '1px solid #93c5fd', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
                background: '#fff', color: '#0f172a',
              }}
            />
          ) : (
            <button
              type="button"
              title="Editar nombre"
              onClick={(e) => { e.stopPropagation(); iniciarEdicionNombreDoc(d); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
                padding: 0, cursor: 'text', fontFamily: 'inherit', color,
                fontSize: 14, fontWeight: 700, lineHeight: 1.35, whiteSpace: 'normal', wordBreak: 'break-word',
              }}
            >
              {d.nombre_documento || d.nombre_original || 'Documento'}
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, opacity: 0.75 }}>✎</span>
            </button>
          )
        );
        return (
          <div
            role="presentation"
            onClick={() => { setLightboxDocIdx(null); setLightboxVistaDoc('parrilla'); setEditandoNombreDocId(null); }}
            style={{
              position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(0,0,0,0.92)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', top: 16, right: 16, display: 'flex', gap: 8, zIndex: 2,
              }}
            >
              <button
                type="button"
                title={lightboxVistaDoc === 'pantalla' ? 'Ver todos los documentos' : 'Ver este documento'}
                onClick={() => setLightboxVistaDoc((v) => (v === 'pantalla' ? 'parrilla' : 'pantalla'))}
                style={{
                  padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.92)', color: '#111', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                }}
              >
                {lightboxVistaDoc === 'pantalla' ? '▦ Lista' : '⛶ Pantalla'}
              </button>
              <button
                type="button"
                onClick={() => { setLightboxDocIdx(null); setLightboxVistaDoc('parrilla'); setEditandoNombreDocId(null); }}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                }}
              >Cerrar</button>
            </div>
            <span style={{
              position: 'absolute', top: 20, left: 16, color: '#fff', fontSize: 12, fontWeight: 700,
              textShadow: '0 1px 4px #000',
            }}>
              Todos los documentos
              {lightboxVistaDoc === 'pantalla' ? ` · ${lightboxDocIdx + 1} / ${documentosCap.length}` : ` · ${documentosCap.length}`}
            </span>

            {lightboxVistaDoc === 'parrilla' ? (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 'min(720px, 96vw)',
                  maxHeight: '82vh',
                  overflowY: 'auto',
                  display: 'flex', flexDirection: 'column', gap: 8,
                  padding: '48px 4px 8px',
                }}
              >
                {documentosCap.map((d, idx) => {
                  const href = resolveCaptacionMediaUrl(d.url);
                  const tipoFila = etiquetaTipoDocumento(d.tipo_codigo) || d.tipo_codigo || '';
                  return (
                    <div
                      key={d.id || `doc-grid-${idx}`}
                      style={{
                        padding: '12px 14px', borderRadius: 12,
                        border: idx === lightboxDocIdx ? '2px solid #93c5fd' : '1px solid rgba(255,255,255,0.18)',
                        background: 'rgba(255,255,255,0.08)',
                      }}
                    >
                      {inputNombre(d)}
                      {tipoFila ? (
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>{tipoFila}</div>
                      ) : null}
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => { setLightboxDocIdx(idx); setLightboxVistaDoc('pantalla'); }}
                          style={{
                            padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                            background: '#fff', color: '#111', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                          }}
                        >
                          Ver
                        </button>
                        {href && (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '7px 12px', borderRadius: 8, textDecoration: 'none',
                              background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700, fontSize: 12,
                            }}
                          >
                            Abrir pestaña
                          </a>
                        )}
                        {d.id && (
                          <button
                            type="button"
                            onClick={() => eliminarDocumentoCap(d.id)}
                            style={{
                              padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                              background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                            }}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                {documentosCap.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditandoNombreDocId(null);
                        setLightboxDocIdx((i) => (i > 0 ? i - 1 : documentosCap.length - 1));
                      }}
                      style={{
                        position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                        width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111',
                        background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                      }}
                    >‹</button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditandoNombreDocId(null);
                        setLightboxDocIdx((i) => (i < documentosCap.length - 1 ? i + 1 : 0));
                      }}
                      style={{
                        position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                        width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111',
                        background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                      }}
                    >›</button>
                  </>
                )}
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    maxWidth: '96vw', maxHeight: '82vh', width: 'min(920px, 96vw)',
                    display: 'flex', flexDirection: 'column', gap: 10,
                  }}
                >
                  <div style={{ color: '#fff' }}>
                    {inputNombre(doc)}
                    {tipo ? (
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>{tipo}</div>
                    ) : null}
                  </div>
                  <div style={{
                    flex: 1, minHeight: kind === 'image' || kind === 'pdf' ? '62vh' : undefined,
                    display: kind === 'image' ? 'flex' : undefined,
                  }}>
                    {kind === 'image' && src ? (
                      <VisorImagenDocumento src={src} title={titulo} />
                    ) : kind === 'pdf' && src ? (
                      <iframe title={titulo} src={src} style={{ width: '100%', height: '62vh', border: 'none', borderRadius: 8, background: '#fff' }} />
                    ) : (
                      <div style={{
                        padding: '28px 32px', borderRadius: 12, background: 'rgba(255,255,255,0.08)', color: '#fff',
                        textAlign: 'center', fontFamily: 'inherit',
                      }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                        {src && (
                          <a href={src} target="_blank" rel="noopener noreferrer" style={{ color: '#93c5fd', fontWeight: 700, fontSize: 14 }}>
                            Abrir documento
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  {doc?.id && (
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {src && (
                        <a
                          href={src}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '10px 18px', borderRadius: 10, textDecoration: 'none',
                            background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700, fontSize: 13,
                          }}
                        >
                          Abrir pestaña
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => eliminarDocumentoCap(doc.id)}
                        style={{
                          padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
                          background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                        }}
                      >
                        Eliminar documento
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })()}
      <ModalRelacionContactoExistente
        open={relacionContacto.open}
        matches={relacionContacto.matches}
        canal={relacionContacto.canal}
        onClose={relacionContacto.dismiss}
        onDescartar={relacionContacto.dismissDiscard}
      />
      <ModalAvisoRetrocesoVisita
        isOpen={!!avisoRetroceso}
        actorNombre={avisoRetroceso?.actor_nombre}
        motivo={avisoRetroceso?.motivo}
        onClose={() => {
          if (avisoRetroceso?.historial_id && usuario?.id) {
            marcarRetrocesoVisitaVisto(usuario.id, avisoRetroceso.historial_id);
          }
          setAvisoRetroceso(null);
        }}
      />
    </div>
  );
}
